/* ==========================================================================
   A09 ASSISTANT · Phase 1 (rule-based, no external AI, no API keys)
   Namespace: window.A09   ·   CSS prefix: .a09-
   Data: Shopify public storefront AJAX endpoints only.

   >>> AI PLUG-IN POINT: A09.ai.understand(text, context) <<<
   Phase 1 runs A09.ai.rules(). To use a real model later, call YOUR backend
   from understand() (never put API keys in theme code) and return the same
   shape that rules() returns. See the block marked "AI PLUG-IN POINT".
   ========================================================================== */
(function () {
  'use strict';

  var A09 = (window.A09 = window.A09 || {});
  if (A09.assistantLoaded) return;
  A09.assistantLoaded = true;

  /* ------------------------------------------------------------------ *
   * Config
   * ------------------------------------------------------------------ */
  var CFG = {};
  try {
    var cfgEl = document.getElementById('a09-assistant-config');
    CFG = cfgEl ? JSON.parse(cfgEl.textContent) : {};
  } catch (e) {
    CFG = {};
  }
  var ROUTES = CFG.routes || {};
  var ROOT = (ROUTES.root || '/').replace(/\/$/, '');
  function route(key, fallback) { return ROUTES[key] || fallback; }
  A09.config = CFG;

  /* v2: replies switched to English (16 Oct 2026). Bumping the key means chats
   * saved by the Hinglish build are not restored; load() also drops the old key. */
  var STORE_KEY = 'a09-assistant-v2';
  var OLD_STORE_KEYS = ['a09-assistant-v1'];
  var MAX_MSGS = 80;
  var BATCH = 6;

  /* ------------------------------------------------------------------ *
   * Small utilities
   * ------------------------------------------------------------------ */
  function $(sel, el) { return (el || document).querySelector(sel); }
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        var v = attrs[k];
        if (v == null || v === false) continue;
        if (k === 'class') n.className = v;
        else if (k === 'text') n.textContent = v;
        else if (k === 'html') n.innerHTML = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') n.addEventListener(k.slice(2), v);
        else n.setAttribute(k, v === true ? '' : v);
      }
    }
    (kids || []).forEach(function (c) {
      if (c == null || c === false) return;
      n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* **bold** only, after escaping */
  function md(s) { return esc(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'); }
  function money(n) {
    var v = Math.round(Number(n) * 100) / 100;
    var whole = v % 1 === 0;
    return (CFG.currencySymbol || '₹') + v.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  }
  function toRupees(p) {
    if (p == null || p === '') return 0;
    if (typeof p === 'number') return p / 100;      /* .js endpoints: paise */
    return parseFloat(p) || 0;                      /* .json endpoints: "1599.00" */
  }
  function img(src, w) {
    if (!src) return '';
    if (typeof src === 'object') src = src.src || src.url || '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 360);
  }
  function stripHtml(h) {
    try {
      var d = new DOMParser().parseFromString(String(h || ''), 'text/html');
      return (d.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (e) { return ''; }
  }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function uniq(a) { return a.filter(function (x, i) { return a.indexOf(x) === i; }); }
  function getJSON(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { Accept: 'application/json' } }, opts || {}))
      .then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (b) {
            var err = new Error(b.description || b.message || ('HTTP ' + r.status));
            err.status = r.status;
            throw err;
          });
        }
        return r.json();
      });
  }
  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  /* ------------------------------------------------------------------ *
   * Session state (sessionStorage, always wrapped)
   * ------------------------------------------------------------------ */
  var state = { msgs: [], ctx: freshCtx(), open: false, cartCount: null };
  function freshCtx() {
    return { filters: null, list: [], offset: 0, shown: [], focus: null, pending: null, seen: {} };
  }
  function save() {
    try {
      if (state.msgs.length > MAX_MSGS) state.msgs = state.msgs.slice(-MAX_MSGS);
      var seenKeys = Object.keys(state.ctx.seen);
      if (seenKeys.length > 60) seenKeys.slice(0, seenKeys.length - 60).forEach(function (k) { delete state.ctx.seen[k]; });
      /* Messages flagged `ephemeral` (order numbers, emails, order and
       * tracking cards) are never written to sessionStorage, so order details
       * are not restored after a reload. */
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        msgs: state.msgs.filter(function (m) { return !m.ephemeral; }),
        ctx: state.ctx,
        open: state.open,
        cartCount: state.cartCount
      }));
    } catch (e) { /* private mode / quota: chat still works in memory */ }
  }
  function load() {
    OLD_STORE_KEYS.forEach(function (k) { try { sessionStorage.removeItem(k); } catch (e) {} });
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return;
      var s = JSON.parse(raw);
      if (s && Array.isArray(s.msgs)) {
        state.msgs = s.msgs;
        state.ctx = Object.assign(freshCtx(), s.ctx || {});
        state.open = !!s.open;
        state.cartCount = s.cartCount == null ? null : s.cartCount;
      }
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Product normalisation (works for .js, .json, suggest & recs)
   * ------------------------------------------------------------------ */
  function normProduct(p) {
    if (!p) return null;
    var variants = (p.variants || []).map(function (v) {
      return {
        id: v.id,
        o: [v.option1, v.option2, v.option3],
        title: v.title,
        price: toRupees(v.price),
        compare: toRupees(v.compare_at_price),
        available: v.available !== false,
        image: v.featured_image ? (v.featured_image.src || v.featured_image) : null
      };
    });
    var options = (p.options || []).map(function (o, i) {
      if (typeof o === 'string') {
        return { name: o, values: uniq(variants.map(function (v) { return v.o[i]; }).filter(Boolean)) };
      }
      return { name: o.name, values: o.values || [] };
    });
    var images = (p.images || []).map(function (im) { return typeof im === 'string' ? im : (im && im.src); }).filter(Boolean);
    var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    var priceMin = variants.length ? Math.min.apply(null, variants.map(function (v) { return v.price; })) : toRupees(p.price_min || p.price);
    var avail = variants.length ? variants.some(function (v) { return v.available; }) : p.available !== false;
    var feat = p.featured_image ? (p.featured_image.url || p.featured_image.src || p.featured_image) : (p.image ? (p.image.src || p.image) : images[0]);
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      url: p.url ? String(p.url).split('?')[0] : (ROOT + '/products/' + p.handle),
      type: p.product_type || p.type || '',
      tags: tags,
      body: p.description || p.body_html || p.body || '',
      image: feat || images[0] || null,
      images: images,
      options: options,
      variants: variants,
      price: priceMin,
      available: avail,
      full: variants.length > 0
    };
  }
  function optIndex(p, re) {
    for (var i = 0; i < p.options.length; i++) if (re.test(p.options[i].name)) return i;
    return -1;
  }
  var RE_SIZE_OPT = /^size$|size/i;
  var RE_COLOR_OPT = /colou?r/i;
  var RE_STYLE_OPT = /^style$|style|type/i;
  function optValues(p, re) { var i = optIndex(p, re); return i < 0 ? [] : p.options[i].values; }

  function variantsMatching(p, want) {
    /* want: { color, size, style } (any may be null) */
    var ci = optIndex(p, RE_COLOR_OPT), si = optIndex(p, RE_SIZE_OPT), ti = optIndex(p, RE_STYLE_OPT);
    return p.variants.filter(function (v) {
      if (want.color && ci > -1 && !colorMatch(v.o[ci], want.color)) return false;
      if (want.size && si > -1 && normSize(v.o[si]) !== normSize(want.size)) return false;
      if (want.style && ti > -1 && styleKind(v.o[ti]) !== want.style) return false;
      return true;
    });
  }
  function colorMatch(value, want) {
    if (!value || !want) return false;
    var v = value.toLowerCase();
    var syn = COLORS[want] || [want];
    return syn.some(function (s) { return v.indexOf(s) > -1; });
  }
  function normSize(s) {
    s = String(s || '').toUpperCase().replace(/\s+/g, '');
    var map = { SMALL: 'S', MEDIUM: 'M', LARGE: 'L', XLARGE: 'XL', 'X-LARGE': 'XL', EXTRALARGE: 'XL', XXLARGE: 'XXL', 'XX-LARGE': 'XXL', '2XL': 'XXL', XXXL: '3XL', XXXXL: '4XL' };
    return map[s] || s;
  }
  /* Style option value → product kind */
  function styleKind(s) {
    s = String(s || '').toLowerCase();
    if (/hood/.test(s)) return 'hoodie';
    if (/sweat/.test(s)) return 'sweatshirt';
    if (/over\s?siz/.test(s) && /(t-?\s?shirt|tee)/.test(s)) return 'oversized_tee';
    if (/(t-?\s?shirt|tee)/.test(s)) return 'tee';
    if (/tote|bag/.test(s)) return 'tote';
    if (/frame|poster/.test(s)) return 'poster';
    if (/coaster/.test(s)) return 'coaster';
    return s ? 'other' : '';
  }
  function productKinds(p) {
    var styles = optValues(p, RE_STYLE_OPT).map(styleKind).filter(Boolean);
    var t = (p.title + ' ' + p.type + ' ' + p.tags.join(' ')).toLowerCase();
    if (!styles.length) {
      if (/hood/.test(t)) styles.push('hoodie');
      else if (/sweat/.test(t)) styles.push('sweatshirt');
      else if (/over\s?siz/.test(t) && /(t-?\s?shirt|tee)/.test(t)) styles.push('oversized_tee');
      else if (/(t-?\s?shirt|\btee)/.test(t)) styles.push('tee');
      else if (/tote/.test(t)) styles.push('tote');
    }
    return uniq(styles);
  }
  function isOversize(p, selectedStyle) {
    var re = /over\s?-?size/i;
    if (re.test(p.title)) return true;
    if (p.tags.some(function (t) { return re.test(t); })) return true;
    if (selectedStyle != null) return re.test(selectedStyle);
    return optValues(p, RE_STYLE_OPT).some(function (v) { return re.test(v); });
  }

  /* ------------------------------------------------------------------ *
   * Storefront API (public AJAX endpoints only) + in-memory cache
   * ------------------------------------------------------------------ */
  var cache = { product: {}, collection: {} };
  var api = {
    product: function (handle) {
      if (cache.product[handle]) return Promise.resolve(cache.product[handle]);
      return getJSON(ROOT + '/products/' + encodeURIComponent(handle) + '.js').then(function (p) {
        var n = normProduct(p);
        cache.product[handle] = n;
        remember(n);
        return n;
      });
    },
    collection: function (handle) {
      if (cache.collection[handle]) return Promise.resolve(cache.collection[handle]);
      return getJSON(ROOT + '/collections/' + encodeURIComponent(handle) + '/products.json?limit=250')
        .then(function (r) {
          var list = (r.products || []).map(normProduct);
          list.forEach(function (n) { if (!cache.product[n.handle]) cache.product[n.handle] = n; });
          cache.collection[handle] = list;
          return list;
        })
        .catch(function () { return []; });
    },
    search: function (q) {
      var url = route('search', ROOT + '/search/suggest.json') + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';
      return getJSON(url).then(function (r) {
        var res = (r.resources && r.resources.results && r.resources.results.products) || [];
        return Promise.all(res.map(function (s) {
          return api.product(s.handle).catch(function () { return normProduct(s); });
        }));
      }).catch(function () { return []; });
    },
    similar: function (productId) {
      var url = route('recommendations', ROOT + '/recommendations/products.json') + '?product_id=' + encodeURIComponent(productId) + '&intent=related&limit=8';
      return getJSON(url).then(function (r) { return (r.products || []).map(normProduct); }).catch(function () { return []; });
    },
    add: function (variantId, qty) {
      return getJSON(route('cartAdd', ROOT + '/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] })
      });
    },
    cart: function () { return getJSON(route('cartJs', ROOT + '/cart.js')); }
  };
  A09.api = api;

  /* ------------------------------------------------------------------ *
   * Analytics: events for GA4, through Shopify Customer Events
   * ------------------------------------------------------------------ *
   * The theme only PUBLISHES events to Shopify (Shopify.analytics.publish).
   * GA4 itself is loaded only inside the "A09 Assistant GA4" custom pixel
   * (analytics/a09-ga4-custom-pixel.js), which also applies the shopper's
   * consent. No GA4 ID and no third-party script live in the theme.
   *
   *   a09_chat_open            panel opened by the shopper
   *   a09_chat_add_to_cart     { product_title, variant_title, price, quantity, currency }
   *   a09_chat_view_cart       { placement }
   *   a09_chat_checkout_click  { cart_value, item_count, currency, placement }
   *
   * After a chatbot add-to-cart the cart gets the hidden attribute
   * _a09_source = "chatbot", so the pixel can spot chatbot orders at
   * checkout_completed. No personal data is ever published.
   *
   * If analytics is off (config), blocked or missing, all of this is a
   * silent no-op and the chat behaves exactly as before.
   * ------------------------------------------------------------------ */
  var ANALYTICS_ON = !!(CFG.analytics && CFG.analytics.enabled === true);
  var SOURCE_ATTR = '_a09_source';
  var NAV_WAIT_MS = 300;      /* longest a checkout/cart click is ever held */
  var lastCart = null;        /* summary of the last /cart.js read */

  function publisher() {
    try {
      var a = window.Shopify && window.Shopify.analytics;
      return a && typeof a.publish === 'function' ? a : null;
    } catch (e) { return null; }
  }
  /** Publish one event. Never throws; returns whether it was handed over. */
  function track(name, data) {
    if (!ANALYTICS_ON) return false;
    try {
      var a = publisher();
      if (!a) return false;
      a.publish(name, data || {});
      return true;
    } catch (e) { return false; }
  }
  A09.track = track;

  function cartSummary(c) {
    return {
      cart_value: Math.round(toRupees(c.total_price) * 100) / 100,
      item_count: Number(c.item_count) || 0,
      currency: c.currency || CFG.currency || 'INR'
    };
  }
  function noteCart(c) {
    if (c && typeof c.item_count === 'number') lastCart = cartSummary(c);
  }

  /** Label the cart as a chatbot cart, once per cart. Errors are swallowed. */
  function markChatCart(c) {
    if (!ANALYTICS_ON) return Promise.resolve();
    if (c && c.attributes && c.attributes[SOURCE_ATTR] === 'chatbot') return Promise.resolve();
    var attrs = {};
    attrs[SOURCE_ATTR] = 'chatbot';
    return getJSON(route('cartUpdate', ROOT + '/cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ attributes: attrs })
    }).then(noteCart, function () {});
  }

  /**
   * Track a click on a chatbot link that leaves the page (checkout / cart).
   * Same-tab clicks are held at most NAV_WAIT_MS so the event is not lost;
   * new-tab / modified clicks are never held. With analytics off or Shopify's
   * publisher missing, the link is left completely alone.
   */
  function trackedNav(a, name, placement) {
    if (!a) return a;
    a.addEventListener('click', function (e) {
      if (!ANALYTICS_ON || !publisher()) return;
      var href = a.getAttribute('href');
      var sameTab = !!href && !(e && (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) &&
        a.getAttribute('target') !== '_blank';
      var started = Date.now();
      var sent = false, gone = false;
      function fire(d) {
        if (sent) return;
        sent = true;
        var data = {};
        if (name === 'a09_chat_checkout_click' && d) {
          data.cart_value = d.cart_value;
          data.item_count = d.item_count;
          data.currency = d.currency;
        }
        data.placement = placement;
        track(name, data);
      }
      function go() {
        if (gone || !sameTab) return;
        gone = true;
        try { location.href = href; } catch (err) {}
      }
      if (sameTab && e && e.preventDefault) e.preventDefault();

      var data = name === 'a09_chat_checkout_click'
        ? Promise.race([
          api.cart().then(function (c) { noteCart(c); return lastCart; }),
          sleep(NAV_WAIT_MS - 120).then(function () { return null; })
        ]).catch(function () { return null; })
        : Promise.resolve(null);
      data.then(function (d) {
        fire(d || lastCart);
        setTimeout(go, Math.max(60, NAV_WAIT_MS - (Date.now() - started)));
      });
      /* Hard cap: the shopper is never held longer than this. */
      setTimeout(function () { fire(lastCart); go(); }, NAV_WAIT_MS + 20);
    });
    return a;
  }

  /* Lightweight record of products the customer has seen (for refs by name) */
  function remember(p) {
    if (!p || !p.handle) return;
    state.ctx.seen[p.handle] = { handle: p.handle, title: p.title, id: p.id };
  }

  /* ------------------------------------------------------------------ *
   * Lexicon (English + Hinglish)
   * ------------------------------------------------------------------ */
  /* Colour → words that appear in variant option values */
  var COLOR_VALUE = { black: ['black'], white: ['white'], beige: ['beige', 'cream'], grey: ['grey', 'gray', 'melange'], navy: ['navy'], blue: ['blue', 'navy'], red: ['red', 'maroon'], green: ['green', 'olive'], pink: ['pink'], yellow: ['yellow', 'mustard'], lavender: ['lavender', 'lilac'] };
  var COLORS = COLOR_VALUE;
  /* Colour words customers type (English + Hinglish) */
  var COLOR_WORDS = {
    black: /\b(black|kala|kaala|kali|kaali|charcoal)\b/,
    white: /\b(white|safed|safaid|off[\s-]?white)\b/,
    beige: /\b(beige|cream|sand|khaki)\b/,
    grey: /\b(gr[ae]y|melange)\b/,
    navy: /\bnavy\b/,
    blue: /\b(blue|neela|nila)\b/,
    red: /\b(red|laal|lal|maroon)\b/,
    green: /\b(green|hara|olive)\b/,
    pink: /\b(pink|gulabi)\b/,
    yellow: /\b(yellow|peela|pila|mustard)\b/,
    lavender: /\b(lavender|lilac)\b/
  };
  var TYPE_WORDS = [
    ['hoodie', /\b(hood(ie|y|ies|i|s)?|hudi|hoody)\b/],
    ['sweatshirt', /\b(sweat\s?-?shirts?|sweaters?|jumpers?)\b/],
    ['tee', /\b(t[\s-]?shirts?|tees?|tshirts?|shirts?|tea\s?shirts?)\b/],
    ['tote', /\b(totes?|tote\s?bags?|bags?|jhola|thaila)\b/],
    ['poster', /\b(posters?|frames?|wall\s?art|prints?)\b/],
    ['coaster', /\b(coasters?)\b/],
    ['postcard', /\b(post\s?cards?)\b/],
    ['calendar', /\b(calendars?)\b/]
  ];
  var THEME_WORDS = [
    ['food', /\b(food|foodie|khana|khaana|khane|snacks?|street\s?food|desi\s?food|chaat)\b/],
    ['mumbai', /\b(mumbai|bombay|bambai|bandra|mumbaikar|amchi|local\s?train|sea\s?link)\b/],
    ['office', /\b(office|corporate|work(ing)?|boss|meeting|email|notice\s?period|wfh|monday)\b/],
    ['dog', /\b(dogs?|doggo|puppy|pupper|kutta|kutte|pet)\b/],
    ['bollywood', /\b(bollywood|filmy|film|movie|movies)\b/],
    ['coffee', /\b(coffee|caffeine|chai|cutting)\b/],
    ['india', /\b(india|varsity|desi)\b/],
    ['new', /\b(new|latest|naya|naye|nayi|fresh\s?drops?|drops?)\b/],
    ['bestseller', /\b(best\s?sellers?|best\s?selling|popular|trending|hit|famous)\b/],
    ['sale', /\b(sale|discount|offers?|deals?)\b/],
    ['kids', /\b(kids?|bachch?[aeo]|children|child)\b/],
    ['plants', /\b(plants?|plant\s?parent)\b/],
    ['techie', /\b(techie|tech|coder|developer|engineer|programmer)\b/],
    ['gym', /\b(gym|workout|fitness)\b/],
    ['travel', /\b(travel|trip|wanderlust)\b/],
    ['vintage', /\b(vintage|retro|old\s?school)\b/],
    ['feels', /\b(feels|feelings|mood|love|pyaar|dil)\b/]
  ];
  var STOP = ('a an the i im i\'m me my mujhe mujhko mera meri mere hum we you u please pls plz bhai bro yaar dude boss ' +
    'show dikha dikhao dikhana dekh dekho dikhaiye batao bata bolo want need chahiye chaiye get find search looking look for ' +
    'some something kuch koi kya kaun kaunsa konsa wala wale wali waala ones one only sirf bas just and or with in of on to ' +
    'mast badhiya accha acha achha nice cool cute good great best awesome mazedaar under below less than upto up max within ' +
    'ke andar se kam tak niche neeche over above more ka ki ko hai hain ho h rs inr price budget cheap sasta saste ' +
    'color colour size sizes fit fits for me any all also bhi and aur dena de do kar karo can could would like is are it this that ' +
    'what which should shall take how does will much wear usually need tell about there here have has am was hai kitna kitne ' +
    'design designs print prints printed stuff things items products product collection range wear').split(/\s+/);

  /* ------------------------------------------------------------------ *
   * Measurement parsing (height, weight, chest)
   * ------------------------------------------------------------------ */
  function parseMeasures(t) {
    var m, out = { heightCm: null, weightKg: null, chestIn: null, spans: [] };
    var s = t.replace(/[’′]/g, "'").replace(/[”″]/g, '"');

    if ((m = s.match(/\b([4-7])\s*(?:'|ft\.?|feet|foot|fit)\s*(?:(\d{1,2}(?:\.\d)?)\s*(?:"|''|in\.?|inch(?:es)?)?)?/i))) {
      var ft = +m[1], inch = m[2] ? +m[2] : 0;
      if (inch < 12) { out.heightCm = Math.round((ft * 12 + inch) * 2.54); out.spans.push(m[0]); }
    } else if ((m = s.match(/\b([4-7])\.(\d{1,2})\s*(?:ft|feet|foot)\b/i))) {
      out.heightCm = Math.round((+m[1] * 12 + +m[2]) * 2.54); out.spans.push(m[0]);
    } else if ((m = s.match(/\b(1[4-9]\d|2[01]\d)\s*(?:cm|cms|centimet(?:er|re)s?)\b/i))) {
      out.heightCm = +m[1]; out.spans.push(m[0]);
    } else if ((m = s.match(/\b(1\.[4-9]\d?|2\.[01]\d?)\s*(?:m|mtr|meters?|metres?)\b/i))) {
      out.heightCm = Math.round(+m[1] * 100); out.spans.push(m[0]);
    } else if ((m = s.match(/\bheight\s*(?:is|hai|:)?\s*([4-7])\.(\d{1,2})\b/i))) {
      out.heightCm = Math.round((+m[1] * 12 + +m[2]) * 2.54); out.spans.push(m[0]);
    }

    if ((m = s.match(/\b(\d{2,3}(?:\.\d)?)\s*(?:kg|kgs|kilo|kilos|kilograms?)\b/i))) {
      out.weightKg = +m[1]; out.spans.push(m[0]);
    } else if ((m = s.match(/\b(\d{2,3})\s*(?:lb|lbs|pounds?)\b/i))) {
      out.weightKg = Math.round(+m[1] * 0.4536); out.spans.push(m[0]);
    } else if ((m = s.match(/\bweigh(?:t|s)?\s*(?:is|hai|:)?\s*(\d{2,3})\b/i))) {
      out.weightKg = +m[1]; out.spans.push(m[0]);
    }

    if ((m = s.match(/\bchest\b[^\d]{0,14}(\d{2,3}(?:\.\d)?)\s*(cm|cms|"|in\b|inch(?:es)?)?/i)) ||
        (m = s.match(/\b(\d{2,3}(?:\.\d)?)\s*(cm|cms|"|in\b|inch(?:es)?)?\s*(?:ki\s+|ka\s+)?chest\b/i))) {
      var v = +m[1], unit = (m[2] || '').toLowerCase();
      if (/cm/.test(unit) || v > 70) v = v / 2.54;
      if (v >= 26 && v <= 70) { out.chestIn = Math.round(v * 10) / 10; out.spans.push(m[0]); }
    }
    return out;
  }
  function fmtHeight(cm) {
    var totalIn = Math.round(cm / 2.54), ft = Math.floor(totalIn / 12), inch = totalIn % 12;
    return ft + "'" + inch;
  }

  /* ------------------------------------------------------------------ *
   * Rule-based understanding → { intent, filters, ref, size, fit, ... }
   * ------------------------------------------------------------------ */
  /* Track Order triggers (English, Hinglish, and Hindi/Marathi in Devanagari).
   * Replies are in English. JS \b does not anchor on Devanagari, so those
   * patterns are matched without it. */
  var RE_ORDER_NOUN = /\b(orders?|parcels?|shipments?|packages?|packets?|couriers?)\b/;
  var RE_TRACK_WORD = /\b(track|tracking|trace|where|wheres|status|kahan|kaha|kidhar|kidhr|locate|arriv(e|ed|ing)|reached|kab\s+(aayega|aaega|ayega|aayegi|aaegi|milega|milegi|pahunchega|pohnchega|tak))\b/;
  var RE_DEVA_ORDER = /(ऑर्डर|आर्डर|ऑडर|पार्सल|कूरियर|कुरियर|पॅकेट|पैकेट)/;
  var RE_DEVA_TRACK = /(कहाँ|कहां|किधर|कुठे|कुठं|ट्रैक|ट्रॅक|स्टेटस|कब\s*(आएगा|आयेगा|आएगी|मिलेगा|पहुँचेगा|पहुंचेगा)|कधी\s*(येईल|मिळेल|पोहोचेल))/;
  function isTrackOrder(t) {
    if (/^\s*(track|tracking|track\s+(my\s+)?orders?|order\s+track(ing)?)\s*$/.test(t)) return true;
    if (RE_ORDER_NOUN.test(t) && RE_TRACK_WORD.test(t)) return true;
    return RE_DEVA_ORDER.test(t) && RE_DEVA_TRACK.test(t);
  }

  function rules(raw, context) {
    context = context || {};
    var text = String(raw || '').trim();
    var t = ' ' + text.toLowerCase()
      .replace(/[‘’\x60]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/₹/g, ' rs ')
      .replace(/(\d),(\d{3})/g, '$1$2')
      .replace(/(\d),(\d{3})/g, '$1$2') + ' ';
    var r = { intent: 'unknown', text: text, filters: {}, refine: false, ref: null, size: null, fit: null, measures: null, keywords: [] };

    /* measurements first so their numbers never become prices */
    var ms = parseMeasures(t);
    var tm = t;
    ms.spans.forEach(function (sp) { tm = tm.replace(sp.toLowerCase(), ' '); });
    if (ms.heightCm || ms.weightKg || ms.chestIn) r.measures = ms;

    /* price */
    var m, f = r.filters;
    var num = '(\\d+(?:\\.\\d+)?)\\s*(k)?';
    var maxRe = new RegExp('(?:under|below|less\\s+than|lower\\s+than|upto|up\\s+to|max(?:imum)?|within|budget(?:\\s+is|\\s+of)?|andar|tak)\\s*(?:of\\s+)?(?:rs\\.?|inr)?\\s*' + num);
    var maxPost = new RegExp('(?:rs\\.?|inr)?\\s*' + num + '\\s*(?:rs\\.?|rupees?|inr)?\\s*(?:ke\\s+andar|ke\\s+niche|ke\\s+neeche|se\\s+kam|tak|ke\\s+under|under|or\\s+less|max|budget)');
    var minRe = new RegExp('(?:above|over|more\\s+than|min(?:imum)?|starting|from)\\s*(?:rs\\.?|inr)?\\s*' + num);
    var minPost = new RegExp(num + '\\s*(?:rs\\.?|rupees?)?\\s*(?:se\\s+(?:upar|zyada|jyada)|plus|and\\s+above|\\+)');
    var bare = /\brs\.?\s*(\d+(?:\.\d+)?)\s*(k)?\b/;
    function val(mm) { var v = parseFloat(mm[1]); if (mm[2]) v *= 1000; return v; }
    if ((m = tm.match(maxRe)) && val(m) >= 100) f.maxPrice = val(m);
    else if ((m = tm.match(maxPost)) && val(m) >= 100) f.maxPrice = val(m);
    if ((m = tm.match(minRe)) && val(m) >= 100 && !/\bover\s*size/.test(tm)) f.minPrice = val(m);
    else if ((m = tm.match(minPost)) && val(m) >= 100) f.minPrice = val(m);
    if (!f.maxPrice && !f.minPrice && (m = tm.match(bare)) && val(m) >= 100) f.maxPrice = val(m);
    if (/\b(cheap|sasta|saste|sasti|budget|affordable)\b/.test(tm) && !f.maxPrice) f.maxPrice = 1000;

    /* colour */
    Object.keys(COLOR_WORDS).forEach(function (c) { if (!f.color && COLOR_WORDS[c].test(tm)) f.color = c; });
    if (/\b(any|koi\s+bhi|all)\s+colou?rs?\b/.test(tm)) f.color = 'any';

    /* type + fit */
    var oversized = /\b(over\s?-?siz(e|ed)|baggy|loose|boxy|drop\s?shoulder)\b/.test(tm);
    TYPE_WORDS.forEach(function (tw) { if (!f.type && tw[1].test(tm)) f.type = tw[0]; });
    if (oversized) {
      f.fit = 'oversized';
      if (!f.type) f.type = 'tee';
    } else if (/\b(regular|normal|slim|classic)\s*fit\b/.test(tm)) f.fit = 'regular';

    /* themes */
    var themes = [];
    THEME_WORDS.forEach(function (tw) { if (tw[1].test(tm)) themes.push(tw[0]); });
    if (themes.indexOf('food') > -1 && themes.indexOf('mumbai') > -1) { f.mumbaiFood = true; themes.splice(themes.indexOf('mumbai'), 1); }
    if (themes.indexOf('coffee') > -1 && themes.indexOf('food') > -1) themes.splice(themes.indexOf('coffee'), 1);
    if (themes.indexOf('india') > -1 && (themes.indexOf('food') > -1 || themes.indexOf('mumbai') > -1)) themes.splice(themes.indexOf('india'), 1);
    if (themes.length) f.theme = themes[0];
    /* typed collection names, e.g. "oh my dog", "phooling around" */
    var LC = (CFG.latestCollections && CFG.latestCollections.length) ? CFG.latestCollections : [{ name: 'Oh My Dog', handle: 'dog-lover' }, { name: 'Phooling Around', handle: 'phooling-around' }, { name: 'Food Basics', handle: 'food-basics' }, { name: 'Office Series', handle: 'office-series' }];
    LC.forEach(function (c) { if (!f.collection && tm.indexOf(' ' + c.name.toLowerCase()) > -1) { f.collection = c.handle; f.collectionName = c.name; } });

    /* size (only with context, so "I'm" never reads as M) */
    var sz = text.match(/\bsize\s*[:\-]?\s*(xxxl|xxl|xl|[2-5]\s?xl|xs|s|m|l|small|medium|large|extra\s?large)\b/i) ||
      text.match(/\b(xxxl|xxl|xl|[2-5]\s?xl|xs|s|m|l|small|medium|large|extra\s?large)\s*(?:size|sized)\b/i) ||
      text.match(/\b(?:in|mein|me|of)\s+(xxxl|xxl|xl|[2-5]xl|xs|s|m|l)\b(?!['’])/i) ||
      text.match(/(?:^|[\s,(])(XXXL|XXL|XL|[2-5]XL|XS|S|M|L)(?=$|[\s,.)!?])/);
    if (sz) {
      var cand = sz[1].replace(/\s+/g, '');
      var before = text.slice(Math.max(0, sz.index - 1), sz.index + 1);
      if (!/['’]/.test(before)) r.size = normSize(cand.replace(/^extralarge$/i, 'XL'));
    }

    /* ordinal / reference */
    var ORD = [
      [1, /\b(first|1st|pehla|pehli|pehle|pahla|pahli|pahile|1\s?(?:no|number))\b|#\s?1\b|\bnumber\s?1\b|\bno\.?\s?1\b/],
      [2, /\b(second|2nd|doosra|dusra|doosri|dusri|dusre|doosre)\b|#\s?2\b|\bnumber\s?2\b/],
      [3, /\b(third|3rd|teesra|tisra|teesri|tisri)\b|#\s?3\b|\bnumber\s?3\b/],
      [4, /\b(fourth|4th|chautha|chauthi)\b|#\s?4\b/],
      [5, /\b(fifth|5th|paanchva|panchva)\b|#\s?5\b/],
      [6, /\b(sixth|6th)\b|#\s?6\b/],
      [-1, /\b(last|aakhri|akhri|last\s?wala)\b/]
    ];
    ORD.forEach(function (o) { if (r.ref == null && o[1].test(tm)) r.ref = o[0]; });
    if (r.ref == null && /\b(this|isko|isse|ise|ye\s?wala|yeh\s?wala|yahi|ye|yeh|it|that|usko|wo\s?wala|same)\b/.test(tm)) r.ref = 'this';

    /* fit preference (size recommendation) */
    if (/\b(snug|tight|fitted|body\s?fit|slim|chipka)\b/.test(tm)) r.fit = 'snug';
    else if (/\b(extra\s?baggy|very\s?(loose|baggy)|super\s?(loose|baggy)|baggy|oversized\s?look|bahut\s?loose)\b/.test(tm)) r.fit = 'baggy';
    else if (/\b(relaxed|regular|normal|comfortable|comfort|loose|easy)\b/.test(tm)) r.fit = 'relaxed';

    /* keywords: leftovers for search */
    var words = tm.replace(/[^a-z0-9\s'-]/g, ' ').split(/\s+/).filter(function (w) {
      return w && w.length > 2 && STOP.indexOf(w) < 0 && !/^\d+k?$/.test(w);
    });
    var known = [];
    TYPE_WORDS.concat(THEME_WORDS).forEach(function (tw) { known.push(tw[1]); });
    Object.keys(COLOR_WORDS).forEach(function (c) { known.push(COLOR_WORDS[c]); });
    r.keywords = words.filter(function (w) {
      var probe = ' ' + w + ' ';
      if (known.some(function (re) { return re.test(probe); })) return false;
      return !/^\d+(st|nd|rd|th)$/.test(w) && !/^(add|cart|details?|similar|view|open|size|sizes|first|second|third|last|pehla|doosra|teesra|dikhao|dikha|ones|hoodies?|tees?|oversized?|baggy|loose|snug|relaxed|checkout|thanks?|hello|hey|hii*)$/.test(w);
    });

    /* ---- intent ---- */
    var has = function (re) { return re.test(tm); };
    var hasFilters = !!(f.type || f.color || f.maxPrice || f.minPrice || f.theme || f.fit || f.mumbaiFood || f.collection);
    var pend = context.pending && context.pending.type;

    if (has(/\b(reset|start\s?over|new\s?chat|clear\s?chat|restart)\b/)) r.intent = 'reset';
    else if (has(/\b(latest|new|naye?|nayi)\s+(collections?|drops?)\b/) && !f.collection) r.intent = 'latest';
    else if (pend === 'fit' && r.fit && !hasFilters) r.intent = 'fit_answer';
    else if (has(/\bsize\s?(chart|guide|table)\b|\bmeasurements?\b/) && !r.measures) r.intent = 'size_chart';
    else if (r.measures || has(/\b(what|which|kaunsa|konsa|kaun\s?sa|kon\s?sa|kya|mera|my|right|best|perfect|sahi|recommend(ed)?|suggest)\s+size\b|\bsize\s+(kya|kaunsa|konsa|batao|bata|suggest|recommend|chahiye|lu|loon|lun|lena)\b|\bfind\s+my\s+size\b|\bsize\s+help\b|\bwill\s+.*\s+fit\s+me\b|\bfit\s+(hoga|karega|aayega)\b/)) r.intent = 'size_rec';
    else if (pend === 'measures' && !hasFilters && /\d/.test(tm)) r.intent = 'size_rec';
    /* Track Order. Tested before checkout, add and shipping: "kab aayega" and
     * "track" also match shipping, which would answer with the general policy.
     * Needs an order noun AND a tracking word, so "shipping kab tak" still gets
     * the policy and "order this" still adds to cart. */
    else if (isTrackOrder(t)) r.intent = 'track_order';
    else if (has(/\b(check\s?out|checkout|pay(ment)?|place\s+order)\b/)) r.intent = 'checkout';
    else if (has(/\b(my\s+cart|cart\s+(dikhao|dikha|show|me\s+kya|mein\s+kya)|show\s+(me\s+)?(my\s+)?cart|view\s+cart|open\s+cart|bag\s+dikhao)\b/)) r.intent = 'show_cart';
    else if (has(/\b(add|daal|dal\s?do|daal\s?do|daalo|dalo|le\s?lo|lelo|le\s?lunga|le\s?lungi|buy|kharid|kharido|order\s+(this|it|kar)|cart\s?(me|mein|main)|into\s+(my\s+)?cart|to\s+(my\s+)?cart|\+\s?cart)\b/)) r.intent = 'add';
    else if (has(/\b(similar|like\s+(this|that|it)|aise|aisa|aisi|jaisa|jaise|jaisi|same\s+(type|vibe|energy|style)|related|more\s+like)\b/)) r.intent = 'similar';
    else if (has(/\b(details?|detail\s?me|batao|bata\s?do|bataiye|tell\s+me\s+(more|about)|more\s+(info|about)|info|describe|description|fabric|material|kapda|specs?|kaisa\s+hai)\b/) && (r.ref != null || has(/\b(of|about|ka|ki|ke)\b/) || context.hasShown)) r.intent = 'details';
    else if (has(/\b(open|view|page|link|dekhna|kholo|khol\s?do)\b/) && r.ref != null) r.intent = 'view';
    else if (has(/\b(show\s+more|more\s+(options|results|please|pls)|aur\s+(dikhao|dikha|batao|options)|next|load\s+more|aur\s+bhi)\b/) || /^\s*(more|aur)\s*$/.test(tm)) r.intent = 'more';
    else if (has(/\b(whatsapp|human|agent|person|call|phone|contact|support|complain|complaint|help\s?line|baat\s+karni)\b/)) r.intent = 'contact';
    else if (has(/\b(exchange|return|refund|replace|wapas)\b/)) r.intent = 'exchange';
    else if (has(/\b(ship(ping)?|deliver(y|ed)?|kab\s+tak|kab\s+aayega|cod|cash\s+on\s+delivery|track)\b/)) r.intent = 'shipping';
    else if (hasFilters || (r.keywords.length && r.keywords.join(' ').length > 2)) r.intent = 'browse';
    else if (has(/\b(hi+|hello|hey+|hola|namaste|namaskar|yo|sup|kaise\s+ho|kya\s+haal)\b/)) r.intent = 'greet';
    else if (has(/\b(thanks?|thank\s?you|thx|ty|shukriya|dhanyavad|great|perfect|awesome|cool|ok(ay)?|done)\b/)) r.intent = 'thanks';
    else if (has(/\b(help|what\s+can\s+you\s+do|kya\s+kar\s+sakte)\b/)) r.intent = 'help';

    /* "only black ones", "under 1500 wale", "sirf oversized" → refine current list */
    if (r.intent === 'browse' && context.hasList) {
      var refineWord = has(/\b(only|sirf|bas|just|filter|in\s+those|unme|inme|un\s?mein|in\s?mein|out\s+of\s+these|those|these|wale|wali|waale|ones)\b/);
      var newScope = f.type || f.theme || f.mumbaiFood || r.keywords.length;
      if (refineWord || !newScope) r.refine = true;
    }
    if (r.intent === 'add' && r.ref == null && !r.size && hasFilters && !context.hasList) r.intent = 'browse';
    return r;
  }

  /* ==================================================================
   * >>> AI PLUG-IN POINT <<<
   * A09.ai.understand(text, context) → Promise<{ intent, filters, ref, size, fit, measures, refine, keywords }>
   * Phase 1: rule-based parser (A09.ai.rules). To plug in a real model later:
   *   return fetch('https://YOUR-BACKEND/a09/understand', { method: 'POST',
   *     headers: { 'Content-Type': 'application/json' },
   *     body: JSON.stringify({ text: text, context: context }) })
   *     .then(function (r) { return r.json(); })
   *     .catch(function () { return A09.ai.rules(text, context); });
   * Keys live on the backend only. Keep the rules() fallback.
   * ================================================================== */
  A09.ai = A09.ai || {};
  A09.ai.rules = rules;
  if (typeof A09.ai.understand !== 'function') {
    A09.ai.understand = function (text, context) {
      return Promise.resolve(A09.ai.rules(text, context));
    };
  }

  /* ------------------------------------------------------------------ *
   * Size engine: height + weight → estimate → REAL A09 chart → one size + reason
   * ------------------------------------------------------------------ */
  var size = {
    chartKeyFor: function (p, selectedStyle) {
      if (!p) return null;
      var kinds = selectedStyle ? [styleKind(selectedStyle)] : productKinds(p);
      var kind = kinds.indexOf('hoodie') > -1 ? 'hoodie'
        : (kinds.indexOf('tee') > -1 || kinds.indexOf('oversized_tee') > -1) ? 'tee'
        : null;
      if (!kind) return null;
      var over = isOversize(p, selectedStyle);
      if (kind === 'tee' && kinds.indexOf('oversized_tee') > -1 && kinds.indexOf('tee') < 0) over = true;
      return kind === 'hoodie' ? (over ? 'oversized_hoodie' : 'hoodie') : (over ? 'oversized_tee' : 'regular_tee');
    },
    estimateChest: function (heightCm, weightKg) {
      var fe = CFG.fitEstimate || {};
      var tbl = fe.chestByWeightKg || [];
      if (!tbl.length || !weightKg) return null;
      var c;
      if (weightKg <= tbl[0][0]) c = tbl[0][1] - (tbl[0][0] - weightKg) * 0.3;
      else if (weightKg >= tbl[tbl.length - 1][0]) c = tbl[tbl.length - 1][1] + (weightKg - tbl[tbl.length - 1][0]) * 0.25;
      else {
        for (var i = 0; i < tbl.length - 1; i++) {
          var a = tbl[i], b = tbl[i + 1];
          if (weightKg >= a[0] && weightKg <= b[0]) { c = a[1] + (b[1] - a[1]) * (weightKg - a[0]) / (b[0] - a[0]); break; }
        }
      }
      if (heightCm) c += (heightCm - (fe.referenceHeightCm || 175)) * (fe.heightAdjustPerCm || 0);
      return Math.round(c * 10) / 10;
    },
    recommend: function (opts) {
      /* opts: { chartKey, heightCm, weightKg, fit, availableSizes } — size from height + weight only */
      var chart = CFG.sizeChart && CFG.sizeChart[opts.chartKey];
      var fe = CFG.fitEstimate || {};
      if (!chart) return null;
      var body = size.estimateChest(opts.heightCm, opts.weightKg); /* height + weight only */
      if (!body) return null;
      var ease = ((fe.ease || {})[opts.chartKey] || {})[opts.fit];
      if (ease == null) ease = 3;
      var target = body + ease;
      var tol = fe.betweenTolerance == null ? 0.5 : fe.betweenTolerance;
      var sizes = chart.sizes.slice(), chest = chart.chest.slice();
      if (opts.availableSizes && opts.availableSizes.length) {
        var keep = opts.availableSizes.map(normSize);
        var s2 = [], c2 = [];
        sizes.forEach(function (s, i) { if (keep.indexOf(normSize(s)) > -1) { s2.push(s); c2.push(chest[i]); } });
        if (s2.length) { sizes = s2; chest = c2; }
      }
      var pick, alt = null, edge = null;
      var exact = -1;
      for (var i = 0; i < chest.length; i++) { if (Math.abs(chest[i] - target) <= tol) { exact = i; break; } }
      if (exact > -1) pick = exact;
      else if (target < chest[0]) { pick = 0; edge = 'small'; }
      else if (target > chest[chest.length - 1]) { pick = chest.length - 1; edge = 'big'; }
      else {
        var lo = 0;
        for (var j = 0; j < chest.length - 1; j++) { if (target > chest[j] && target < chest[j + 1]) { lo = j; break; } }
        var hi = lo + 1;
        if (opts.fit === 'snug') { pick = lo; alt = hi; } else { pick = hi; alt = lo; }
      }
      return {
        size: sizes[pick],
        alt: alt == null ? null : sizes[alt],
        edge: edge,
        chartKey: opts.chartKey,
        chart: chart,
        garmentChest: chest[pick],
        bodyChest: body,
        target: Math.round(target * 10) / 10,
        usedChest: !!opts.chestIn
      };
    }
  };
  A09.size = size;

  /* ------------------------------------------------------------------ *
   * DOM refs
   * ------------------------------------------------------------------ */
  var root, launcher, panel, log, form, input, countEls;
  var busy = false;

  /* ------------------------------------------------------------------ *
   * Message model → render
   * ------------------------------------------------------------------ */
  function push(msg, opts) {
    state.msgs.push(msg);
    save();
    renderMsg(msg, true);
    if (!opts || !opts.noScroll) scrollDown();
  }
  function bot(t, ephemeral) { push({ k: 'text', from: 'bot', t: t, ephemeral: !!ephemeral || undefined }); }
  function user(t, ephemeral) { push({ k: 'text', from: 'user', t: t, ephemeral: !!ephemeral || undefined }); }
  function chips(items, cls) { push({ k: 'chips', items: items, cls: cls || '' }); }

  function scrollDown() {
    if (!log) return;
    requestAnimationFrame(function () { log.scrollTop = log.scrollHeight; });
  }
  function rowEl(from, isNew) {
    return el('div', { class: 'a09-row a09-row--' + (from || 'bot') + (isNew && !reduceMotion ? ' is-new' : '') });
  }

  function renderMsg(m, isNew) {
    var row;
    switch (m.k) {
      case 'text':
        row = rowEl(m.from, isNew);
        row.appendChild(el('div', { class: 'a09-bubble', html: md(m.t) }));
        break;
      case 'context':
        row = el('p', { class: 'a09-context', text: m.t });
        break;
      case 'chips':
        row = rowEl('bot', isNew);
        row.appendChild(renderChips(m.items, m.cls));
        break;
      case 'cards':
        row = rowEl('bot', isNew);
        row.appendChild(renderCards(m.items));
        break;
      case 'details':
        row = rowEl('bot', isNew);
        row.appendChild(renderDetails(m.p));
        break;
      case 'rec':
        row = rowEl('bot', isNew);
        row.appendChild(renderRec(m));
        break;
      case 'chart':
        row = rowEl('bot', isNew);
        row.appendChild(renderChart(m.key));
        break;
      case 'confirm':
        row = rowEl('bot', isNew);
        row.appendChild(renderConfirm(m));
        break;
      case 'contact':
        row = rowEl('bot', isNew);
        row.appendChild(renderContact());
        break;
      case 'orders':
        row = rowEl('bot', isNew);
        row.appendChild(renderOrders(m.orders || []));
        break;
      case 'tracking':
        row = rowEl('bot', isNew);
        row.appendChild(renderTracking(m.order || {}, m.only || null));
        break;
      default:
        return;
    }
    log.appendChild(row);
  }

  function renderChips(items, cls) {
    var wrap = el('div', { class: 'a09-chips ' + (cls || ''), role: 'group', 'aria-label': 'Quick replies' });
    items.forEach(function (c) {
      if (c.href) {
        var link = el('a', { class: 'a09-chip a09-chip--link' + (c.wa ? ' a09-chip--wa' : ''), href: c.href, target: c.blank ? '_blank' : null, rel: c.blank ? 'noopener' : null, text: c.label });
        if (c.ev) trackedNav(link, c.ev, 'chip');
        wrap.appendChild(link);
        return;
      }
      var b = el('button', {
        type: 'button',
        class: 'a09-chip' + (c.size ? ' a09-chip--size' : ''),
        disabled: c.disabled ? true : null,
        'aria-label': c.aria || null,
        text: c.label
      });
      b.addEventListener('click', function () { onChip(c); });
      wrap.appendChild(b);
    });
    return wrap;
  }

  function cardImage(p) { return img(p.image, 400); }
  function renderCards(items) {
    var wrap = el('div', { class: 'a09-cards', role: 'list', 'aria-label': 'Products' });
    items.forEach(function (p, i) {
      var badge = el('span', { class: 'a09-badge' + (p.available ? '' : ' a09-badge--out'), text: p.available ? 'In stock' : 'Sold out' });
      var media = el('a', { class: 'a09-card__media', href: p.url, tabindex: '-1', 'aria-hidden': 'true' }, [
        p.image ? el('img', { src: cardImage(p), alt: '', loading: 'lazy', width: '400', height: '400' }) : null,
        badge
      ]);
      var titleId = 'a09-t-' + p.handle + '-' + Math.random().toString(36).slice(2, 7);
      var price = el('div', { class: 'a09-price' }, [money(p.price)]);
      if (p.compare && p.compare > p.price) price.appendChild(el('s', { text: money(p.compare) }));
      var name = p.title;
      var actions = el('div', { class: 'a09-card__actions' }, [
        el('a', { class: 'a09-btn', href: p.url, 'aria-label': 'View ' + name, text: 'View' }),
        el('button', { type: 'button', class: 'a09-btn', 'aria-label': 'Details of ' + name, text: 'Details', onclick: function () { act({ do: 'details', handle: p.handle }, 'Details'); } }),
        el('button', { type: 'button', class: 'a09-btn', 'aria-label': 'Similar to ' + name, text: 'Similar', onclick: function () { act({ do: 'similar', handle: p.handle }, 'Similar'); } }),
        el('button', { type: 'button', class: 'a09-btn a09-btn--solid', 'aria-label': 'Add ' + name + ' to cart', disabled: p.available ? null : true, text: '+ Cart', onclick: function () { act({ do: 'add', handle: p.handle, color: p.color || null }, '+ Cart'); } })
      ]);
      var card = el('article', { class: 'a09-card', role: 'listitem', 'aria-labelledby': titleId, style: '--i:' + i }, [
        media,
        el('div', { class: 'a09-card__body' }, [
          el('h3', { class: 'a09-card__title', id: titleId, text: name }),
          price,
          actions
        ])
      ]);
      wrap.appendChild(card);
    });
    return wrap;
  }

  function renderDetails(d) {
    var sizes = el('div', { class: 'a09-sizes', role: 'list' });
    (d.sizes || []).forEach(function (s) {
      sizes.appendChild(el('span', { class: 'a09-size' + (s.ok ? '' : ' is-out'), role: 'listitem', 'aria-label': s.label + (s.ok ? ' in stock' : ' sold out'), text: s.label }));
    });
    var meta = el('div', { class: 'a09-details__meta' }, [
      d.colors && d.colors.length ? el('div', null, [el('span', { class: 'a09-label', text: 'Colours · ' }), document.createTextNode(d.colors.join(', '))]) : null,
      d.sizes && d.sizes.length ? el('span', { class: 'a09-label', text: 'Sizes' }) : null,
      d.sizes && d.sizes.length ? sizes : null,
      el('div', { class: 'a09-details__cta' }, [
        el('button', { type: 'button', class: 'a09-btn a09-btn--solid a09-btn--lg', disabled: d.available ? null : true, text: d.available ? '+ Add to cart' : 'Sold out', onclick: function () { act({ do: 'add', handle: d.handle, color: d.color || null }, '+ Cart'); } }),
        d.sizeable ? el('button', { type: 'button', class: 'a09-btn a09-btn--lg', text: 'Find my size', onclick: function () { act({ do: 'sizeFor', handle: d.handle }, 'Find my size'); } }) : null,
        el('a', { class: 'a09-btn a09-btn--lg', href: d.url, text: 'View page' })
      ])
    ]);
    return el('div', { class: 'a09-panelcard' }, [
      el('div', { class: 'a09-details' }, [
        d.image ? el('img', { src: img(d.image, 200), alt: '', loading: 'lazy', width: '84', height: '84' }) : el('span'),
        el('div', null, [
          el('h3', { text: d.title }),
          el('div', { class: 'a09-price' }, [money(d.price)]),
          d.desc ? el('p', { text: d.desc }) : null
        ]),
        meta
      ])
    ]);
  }

  function renderRec(m) {
    var card = el('div', { class: 'a09-panelcard' }, [
      el('div', { class: 'a09-rec' }, [
        el('div', { class: 'a09-rec__size', 'aria-hidden': 'true', text: m.size }),
        el('div', null, [
          el('span', { class: 'a09-label a09-rec__label', text: 'Recommended size' }),
          el('p', { class: 'a09-rec__why', html: '<span class="a09-sr">' + esc(m.size) + '. </span>' + md(m.why) })
        ])
      ])
    ]);
    var frag = el('div', { style: 'display:flex;flex-direction:column;gap:10px;width:100%' }, [card]);
    if (m.pills && m.pills.length) {
      var pills = el('div', { class: 'a09-bubble a09-pills' }, [el('span', { text: 'Why:' })]);
      m.pills.forEach(function (p, i) { pills.appendChild(el('span', { class: 'a09-pill' + (i === m.pills.length - 1 ? ' a09-pill--green' : ''), text: p })); });
      frag.appendChild(pills);
    }
    if (m.alt) frag.appendChild(el('div', { class: 'a09-bubble', html: md(m.alt) }));
    var cta = m.ctaSize === undefined ? m.size : m.ctaSize;
    frag.appendChild(el('div', { class: 'a09-bubble', html: md((CFG.policy && CFG.policy.exchange || "Not the right size? Returns are accepted within 14 days of receiving your order.") + (m.handle && cta ? ' Add ' + cta + ' to cart?' : '')) }));
    if (m.handle && cta) {
      frag.appendChild(el('div', { class: 'a09-chips' }, [
        el('button', { type: 'button', class: 'a09-btn a09-btn--hot a09-btn--lg', text: 'Add ' + cta + ' to cart', onclick: function () { act({ do: 'addSize', handle: m.handle, size: cta, color: m.color || null, style: m.style || null }, 'Add ' + cta + ' to cart'); } }),
        m.altSize ? el('button', { type: 'button', class: 'a09-btn a09-btn--lg', text: 'Add ' + m.altSize + ' instead', onclick: function () { act({ do: 'addSize', handle: m.handle, size: m.altSize, color: m.color || null, style: m.style || null }, 'Add ' + m.altSize); } }) : null
      ]));
    }
    return frag;
  }

  function renderChart(key) {
    var c = CFG.sizeChart && CFG.sizeChart[key];
    if (!c) return el('div', { class: 'a09-bubble', text: 'Size chart not available.' });
    var head = el('tr', null, [el('th', { scope: 'col', text: 'Inches' })].concat(c.sizes.map(function (s) { return el('th', { scope: 'col', text: s }); })));
    var rows = ['chest', 'shoulder', 'length'].filter(function (k) { return c[k]; }).map(function (k) {
      return el('tr', null, [el('th', { scope: 'row', text: cap(k) })].concat(c[k].map(function (v) { return el('td', { text: v == null ? '–' : String(v) }); })));
    });
    return el('div', { class: 'a09-panelcard a09-chart' }, [
      el('h3', { text: 'A09 size chart · ' + (c.title || c.label) }),
      el('div', { class: 'a09-chart__scroll' }, [
        el('table', null, [el('caption', { text: 'Garment measurements in inches.' }), el('thead', null, [head]), el('tbody', null, rows)])
      ])
    ]);
  }

  function renderConfirm(m) {
    return el('div', { class: 'a09-panelcard' }, [
      el('div', { class: 'a09-confirm', role: 'status' }, [
        el('span', { class: 'a09-confirm__tick', 'aria-hidden': 'true', html: '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>' }),
        el('div', null, [
          el('p', { html: 'Added <strong>' + esc(m.title) + ' · ' + esc(m.size) + ' · ' + esc(money(m.price)) + '</strong>. Go to checkout?' }),
          el('div', { class: 'a09-confirm__cta' }, [
            trackedNav(el('a', { class: 'a09-btn a09-btn--hot a09-btn--lg', href: route('checkout', '/checkout'), text: 'Checkout →' }), 'a09_chat_checkout_click', 'confirm'),
            trackedNav(el('a', { class: 'a09-btn a09-btn--lg', href: route('cart', '/cart'), text: 'View cart' }), 'a09_chat_view_cart', 'confirm'),
            el('button', { type: 'button', class: 'a09-btn a09-btn--lg', text: 'Keep shopping', onclick: function () { input && input.focus(); } })
          ])
        ])
      ])
    ]);
  }

  function renderContact() {
    var c = CFG.contact || {};
    return renderChips([
      c.whatsapp ? { label: '💬 ' + (c.whatsappLabel || 'Chat with us on WhatsApp'), href: c.whatsapp, blank: true, wa: true } : null,
      c.email ? { label: '✉️ ' + c.email, href: 'mailto:' + c.email } : null
    ].filter(Boolean));
  }

  /* Typing indicator */
  var typingEl = null;
  function typing(on) {
    if (on) {
      if (typingEl) return;
      typingEl = rowEl('bot', true);
      typingEl.appendChild(el('div', { class: 'a09-bubble a09-typing', role: 'status', 'aria-label': 'A09 is typing' }, [el('span'), el('span'), el('span')]));
      log.appendChild(typingEl);
      scrollDown();
    } else if (typingEl) {
      typingEl.remove();
      typingEl = null;
    }
  }
  function think(promise, minMs) {
    typing(true);
    return Promise.all([promise, sleep(reduceMotion ? 150 : (minMs || 550))]).then(function (r) { typing(false); return r[0]; }, function (e) { typing(false); throw e; });
  }

  /* ------------------------------------------------------------------ *
   * Cart count (launcher, header and the theme's own bubbles)
   * ------------------------------------------------------------------ */
  function setCount(n) {
    state.cartCount = n;
    save();
    (countEls || []).forEach(function (c) {
      c.textContent = n > 99 ? '99+' : String(n);
      c.hidden = !n;
    });
    if (launcher) launcher.setAttribute('aria-label', 'Open A09 shopping assistant' + (n ? ' · ' + n + ' in cart' : ''));
  }
  function refreshCart(fromAdd) {
    return api.cart().then(function (c) {
      noteCart(c);
      setCount(c.item_count || 0);
      if (fromAdd) syncThemeCart(c);
      return c;
    }).catch(function () {});
  }
  function syncThemeCart(c) {
    var n = c.item_count || 0;
    try {
      var sel = '[data-cart-count], .cart-count, .sf-cart-count, .m-cart-count-bubble, .cart-count-bubble span, [data-cart-item-count], .header__cart-count';
      document.querySelectorAll(sel).forEach(function (node) {
        if (node.closest('.a09-root')) return;
        if (node.children.length === 0) node.textContent = n;
        if ('hidden' in node && n > 0) node.hidden = false;
      });
      ['cart:refresh', 'cart:updated', 'theme:cart:change'].forEach(function (ev) {
        document.dispatchEvent(new CustomEvent(ev, { bubbles: true, detail: { cart: c, source: 'a09-assistant' } }));
      });
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Product resolution helpers
   * ------------------------------------------------------------------ */
  function pageProduct() { return CFG.product || null; }
  function selectedVariantId() {
    try {
      var f = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (f && f.value) return Number(f.value);
      var q = new URLSearchParams(location.search).get('variant');
      if (q) return Number(q);
    } catch (e) {}
    return CFG.product ? CFG.product.selectedVariantId : null;
  }
  function selectedStyleOf(p) {
    if (!pageProduct() || !p || p.handle !== pageProduct().handle) return null;
    var id = selectedVariantId(), ti = optIndex(p, RE_STYLE_OPT);
    if (ti < 0) return null;
    var v = p.variants.filter(function (x) { return x.id === id; })[0];
    return v ? v.o[ti] : null;
  }
  function selectedColorOf(p) {
    if (!pageProduct() || !p || p.handle !== pageProduct().handle) return null;
    var id = selectedVariantId(), ci = optIndex(p, RE_COLOR_OPT);
    if (ci < 0) return null;
    var v = p.variants.filter(function (x) { return x.id === id; })[0];
    if (!v) return null;
    var val = String(v.o[ci] || '').toLowerCase();
    var hit = null;
    Object.keys(COLOR_VALUE).forEach(function (k) { if (!hit && COLOR_VALUE[k].some(function (s) { return val.indexOf(s) > -1; })) hit = k; });
    return hit;
  }
  function resolveRef(u) {
    var ctx = state.ctx;
    if (typeof u.ref === 'number' && ctx.shown.length) {
      var i = u.ref === -1 ? ctx.shown.length - 1 : u.ref - 1;
      return ctx.shown[i] || null;
    }
    /* by name, e.g. "Misal Pav ka similar" */
    var words = (u.keywords || []).filter(function (w) { return w.length > 2; });
    if (words.length) {
      var best = null, bestScore = 0;
      var pool = ctx.shown.concat(Object.keys(ctx.seen).map(function (k) { return ctx.seen[k].handle; }));
      uniq(pool).forEach(function (h) {
        var title = ((ctx.seen[h] && ctx.seen[h].title) || h).toLowerCase();
        var score = words.filter(function (w) { return title.indexOf(w) > -1; }).length;
        if (score > bestScore) { best = h; bestScore = score; }
      });
      if (best) return best;
    }
    if (u.ref === 'this' || u.ref == null) {
      if (pageProduct()) return pageProduct().handle;
      if (ctx.focus) return ctx.focus;
      if (ctx.shown.length === 1) return ctx.shown[0];
    }
    return null;
  }
  function cardData(p, color) {
    var image = p.image, price = p.price, available = p.available, compare = 0;
    var vs = color ? variantsMatching(p, { color: color }) : p.variants;
    if (vs.length) {
      var withImg = vs.filter(function (v) { return v.image; })[0];
      if (color && withImg) image = withImg.image;
      price = Math.min.apply(null, vs.map(function (v) { return v.price; }));
      available = vs.some(function (v) { return v.available; });
      var cheapest = vs.filter(function (v) { return v.price === price; })[0];
      compare = cheapest ? cheapest.compare : 0;
    }
    return { handle: p.handle, title: p.title, url: p.url, image: image, price: price, compare: compare, available: available, color: color || null, id: p.id };
  }

  /* ------------------------------------------------------------------ *
   * Browse: pick source → filter → rank
   * ------------------------------------------------------------------ */
  /* Latest Collections: edit the list in the snippet config ("latestCollections") */
  var LATEST = (CFG.latestCollections && CFG.latestCollections.length) ? CFG.latestCollections : [
    { name: 'Oh My Dog', handle: 'dog-lover' },
    { name: 'Phooling Around', handle: 'phooling-around' },
    { name: 'Food Basics', handle: 'food-basics' },
    { name: 'Office Series', handle: 'office-series' }
  ];
  function showLatest() {
    bot('Fresh drops 🔥 Which collection would you like to see?');
    chips(LATEST.map(function (c) { return { label: c.name, act: { do: 'collection', handle: c.handle, name: c.name } }; }));
  }
  function collectionsFor(f) {
    var C = CFG.collections || {};
    var out = [];
    function addC(v) { (Array.isArray(v) ? v : [v]).forEach(function (h) { if (h && out.indexOf(h) < 0) out.push(h); }); }
    if (f.collection) addC(f.collection);
    else if (f.mumbaiFood) addC(C.food);
    else if (f.theme && C[f.theme]) addC(C[f.theme]);
    if (!out.length && f.type) {
      if (f.type === 'tee' && f.fit === 'oversized') addC(C.oversized_tee);
      else if (C[f.type]) addC(C[f.type]);
    }
    return out;
  }
  function matchesFilters(p, f) {
    if (f.type) {
      var kinds = productKinds(p);
      if (f.type === 'tee') {
        if (f.fit === 'oversized') { if (!(kinds.indexOf('oversized_tee') > -1 || (kinds.indexOf('tee') > -1 && isOversize(p)))) return false; }
        else if (kinds.indexOf('tee') < 0 && kinds.indexOf('oversized_tee') < 0) return false;
      } else if (kinds.indexOf(f.type) < 0) {
        if (!(f.type === 'poster' && /poster|frame/i.test(p.title + ' ' + p.tags.join(' ')))) return false;
      }
      if (f.type === 'hoodie' && f.fit === 'oversized' && !isOversize(p)) return false;
    } else if (f.fit === 'oversized' && !isOversize(p)) return false;
    if (f.color && f.color !== 'any') {
      var ci = optIndex(p, RE_COLOR_OPT);
      var vals = ci > -1 ? p.options[ci].values : [];
      var okVal = vals.some(function (v) { return colorMatch(v, f.color); });
      if (!okVal && !(ci < 0 && COLOR_WORDS[f.color] && COLOR_WORDS[f.color].test(p.title.toLowerCase()))) return false;
    }
    var vs = relevantVariants(p, f);
    var price = vs.length ? Math.min.apply(null, vs.map(function (v) { return v.price; })) : p.price;
    if (f.maxPrice && price > f.maxPrice) return false;
    if (f.minPrice && price < f.minPrice) return false;
    return true;
  }
  function relevantVariants(p, f) {
    var want = { color: f.color && f.color !== 'any' ? f.color : null };
    if (f.type === 'hoodie') want.style = 'hoodie';
    var vs = variantsMatching(p, want);
    return vs.length ? vs : p.variants;
  }
  function designOf(title) { return String(title || '').split(/\s+[-–—|]\s+/)[0].trim().toLowerCase(); }
  function dedupeDesigns(list) {
    var seen = {};
    return list.filter(function (p) { var k = designOf(p.title); if (seen[k]) return false; seen[k] = 1; return true; });
  }
  function rank(list, f, keywords) {
    var food = (CFG.mumbaiFood || []).map(function (s) { return s.toLowerCase(); });
    return list.map(function (p, i) {
      var t = (p.title + ' ' + p.tags.join(' ')).toLowerCase();
      var s = 0;
      if (p.available) s += 100;
      if (f.mumbaiFood) s += food.filter(function (w) { return t.indexOf(w) > -1; }).length * 30;
      (keywords || []).forEach(function (k) { if (t.indexOf(k) > -1) s += 20; });
      if (f.color && f.color !== 'any' && t.indexOf(f.color) > -1) s += 8;
      if (!f.type) { var k = productKinds(p); if (k.indexOf('tee') > -1) s += 15; else if (k.indexOf('oversized_tee') > -1 || k.indexOf('hoodie') > -1) s += 10; }
      return { p: p, s: s - i * 0.01 };
    }).sort(function (a, b) { return b.s - a.s; }).map(function (x) { return x.p; });
  }
  function findProducts(f, keywords) {
    var cols = collectionsFor(f);
    var kw = (keywords || []).join(' ').trim();
    var src;
    if (cols.length) src = Promise.all(cols.map(api.collection)).then(function (ls) { return [].concat.apply([], ls); });
    else if (kw) src = api.search(kw);
    else src = api.collection((CFG.collections && CFG.collections.all) || 'all');
    return src.then(function (list) {
      var seen = {};
      list = list.filter(function (p) { if (!p || seen[p.handle]) return false; seen[p.handle] = 1; return true; });
      var hits = list.filter(function (p) { return matchesFilters(p, f); });
      /* free-text search: predictive search is fuzzy, so keep only real keyword hits */
      if (!cols.length && kw) {
        hits = hits.filter(function (p) {
          var t = (p.title + ' ' + p.handle + ' ' + p.tags.join(' ')).toLowerCase();
          return keywords.some(function (k) { return k.length > 2 && t.indexOf(k) > -1; });
        });
      }
      /* theme collection + type returned nothing: fall back to the type collection + keyword */
      if (!hits.length && cols.length && f.type && (f.theme || f.mumbaiFood)) {
        var f2 = Object.assign({}, f); delete f2.theme; delete f2.mumbaiFood;
        return findProducts(f2, keywords.concat(f.theme ? [f.theme] : ['food']));
      }
      if (kw && cols.length && hits.length > 6) {
        var narrowed = hits.filter(function (p) { return keywords.some(function (k) { return (p.title + ' ' + p.tags.join(' ')).toLowerCase().indexOf(k) > -1; }); });
        if (narrowed.length) hits = narrowed;
      }
      hits = rank(hits, f, keywords);
      return f.type ? hits : dedupeDesigns(hits);
    });
  }

  function describeFilters(f, count) {
    var parts = [];
    if (f.color && f.color !== 'any') parts.push(f.color);
    if (f.fit === 'oversized') parts.push('oversized');
    var noun = { hoodie: 'hoodie', sweatshirt: 'sweatshirt', tee: 'tee', tote: 'tote', poster: 'poster', coaster: 'coaster', postcard: 'postcard', calendar: 'calendar' }[f.type] || 'pick';
    parts.push(noun + (count === 1 ? '' : 's'));
    var s = parts.join(' ');
    if (f.maxPrice) s += ' under ' + money(f.maxPrice);
    if (f.minPrice) s += ' above ' + money(f.minPrice);
    return s;
  }

  function showBatch(intro) {
    var ctx = state.ctx;
    var batch = ctx.list.slice(ctx.offset, ctx.offset + BATCH);
    ctx.offset += batch.length;
    var color = ctx.filters && ctx.filters.color !== 'any' ? ctx.filters.color : null;
    /* after a page change the in-memory cache is empty: refetch what we show */
    return Promise.all(batch.map(function (h) {
      return cache.product[h] ? Promise.resolve(cache.product[h]) : api.product(h).catch(function () { return null; });
    })).then(function (ps) {
      var data = ps.filter(Boolean).map(function (p) { return cardData(p, color); });
      ctx.shown = data.map(function (d) { return d.handle; });
      data.forEach(function (d) { ctx.seen[d.handle] = { handle: d.handle, title: d.title, id: d.id }; });
      if (intro) bot(intro);
      if (!data.length) { bot('Couldn’t load the products 😕 Please try again.'); return; }
      push({ k: 'cards', items: data });
      var more = ctx.list.length - ctx.offset;
      var next = [];
      if (more > 0) next.push({ label: 'Show ' + Math.min(more, BATCH) + ' more', text: 'Show more' });
      if (ctx.filters && !ctx.filters.color && data.length > 1) next.push({ label: 'Only black', text: 'Only black ones' });
      if (ctx.filters && !ctx.filters.maxPrice && data.length > 1) next.push({ label: 'Under ₹1,500', text: 'Only under ₹1500' });
      next.push({ label: 'Find my size', text: 'Find my size' });
      chips(next.slice(0, 4));
      save();
    });
  }

  /* ------------------------------------------------------------------ *
   * Handlers
   * ------------------------------------------------------------------ */
  function handleBrowse(u) {
    var ctx = state.ctx;
    var f;
    if (u.refine && ctx.filters) {
      f = Object.assign({}, ctx.filters);
      Object.keys(u.filters).forEach(function (k) { if (u.filters[k] != null) f[k] = u.filters[k]; });
      if (u.filters.color === 'any') delete f.color;
    } else {
      f = Object.assign({}, u.filters);
    }
    var keywords = u.refine ? (ctx.keywords || []) : u.keywords;
    return think(findProducts(f, keywords)).then(function (list) {
      ctx.filters = f;
      ctx.keywords = keywords;
      ctx.list = list.map(function (p) { return p.handle; }).slice(0, 48);
      ctx.offset = 0;
      ctx.pending = null;
      save();
      var anyFilter = Object.keys(f).some(function (k) { return f[k] != null; });
      if (!list.length && !anyFilter) return fallback();
      if (!list.length) {
        bot('Hmm, couldn’t find ' + (describeFilters(f, 2) === 'picks' ? 'that' : describeFilters(f, 2)) + ' right now 😅 Want to loosen the filters a bit?');
        var relax = [];
        if (f.color) relax.push({ label: 'Any colour', text: 'any colour' });
        if (f.maxPrice) relax.push({ label: 'No price limit', text: 'Show ' + (f.type ? f.type + 's' : 'all') + ' without price limit', act: { do: 'relax', drop: 'maxPrice' } });
        relax.push({ label: 'Hoodies', text: 'Hoodies' }, { label: 'Latest Collections', act: { do: 'latest' } }, { label: 'Bestsellers', text: 'Bestsellers' });
        chips(relax.slice(0, 4));
        return;
      }
      list.forEach(function (p) { if (!cache.product[p.handle]) cache.product[p.handle] = p; });
      var first = list[0];
      var firstCard = cardData(first, f.color && f.color !== 'any' ? f.color : null);
      var n = list.length;
      var intro;
      if (u.refine) {
        intro = 'Done — ' + n + ' ' + describeFilters(f, n) + '. **' + firstCard.title + '** (' + money(firstCard.price) + ') is up first.';
      } else if (f.collection && f.collectionName) {
        intro = '**' + f.collectionName + '** collection 👇 ' + n + ' design' + (n === 1 ? '' : 's') + ':';
      } else if (f.mumbaiFood || f.theme === 'food') {
        intro = 'The real taste of Mumbai, now wearable 🤤 Here ' + (n === 1 ? 'is one' : 'are ' + Math.min(n, BATCH) + ' favourites') + ':';
      } else if (f.theme === 'mumbai') {
        intro = 'From Bandra, with love 🌊 ' + n + ' Mumbai designs:';
      } else if (f.theme === 'office') {
        intro = 'For every meeting that could’ve been an email 💼 ' + n + ' office picks:';
      } else if (f.type === 'hoodie') {
        intro = 'Hoodie season ON 🔥 ' + n + ' ' + describeFilters(f, n) + ':';
      } else if (f.theme === 'bestseller') {
        intro = 'Crowd favourites, straight from the drop 🙌';
      } else {
        intro = 'Here you go! ' + n + ' ' + describeFilters(f, n) + ':';
      }
      return showBatch(intro);
    }).catch(function () { failSoft(); });
  }

  function handleMore() {
    var ctx = state.ctx;
    if (!ctx.list.length || ctx.offset >= ctx.list.length) {
      bot('That’s everything for now 🙂 Want to try something else?');
      return greetChips();
    }
    return think(Promise.resolve()).then(function () { return showBatch('Here are more 👇'); });
  }

  function needProduct(u, verb) {
    var h = resolveRef(u);
    if (!h) {
      bot(state.ctx.shown.length ? 'Which one? Say “first one”, “2nd” or the name 🙂' : 'Let me show you something first. What are you looking for?');
      if (!state.ctx.shown.length) greetChips();
      return null;
    }
    state.ctx.focus = h;
    return h;
  }

  function handleDetails(handle) {
    return think(api.product(handle)).then(function (p) {
      state.ctx.focus = p.handle;
      var color = state.ctx.filters && state.ctx.filters.color !== 'any' ? state.ctx.filters.color : selectedColorOf(p);
      var d = cardData(p, color);
      var si = optIndex(p, RE_SIZE_OPT);
      var sizes = si < 0 ? [] : p.options[si].values.map(function (s) {
        var vs = variantsMatching(p, { size: s, color: color });
        return { label: s, ok: vs.some(function (v) { return v.available; }) };
      }).filter(function (s) { return s.ok; });
      var desc = stripHtml(p.body);
      if (desc.length > 260) desc = desc.slice(0, 257).replace(/\s+\S*$/, '') + '…';
      push({ k: 'details', p: {
        handle: p.handle, title: p.title, url: p.url, image: d.image, price: d.price, available: d.available, color: color,
        desc: desc, sizes: sizes, colors: optValues(p, RE_COLOR_OPT), sizeable: !!size.chartKeyFor(p, selectedStyleOf(p))
      } });
      save();
    }).catch(function () { failSoft(); });
  }

  function handleSimilar(handle) {
    return think(api.product(handle).then(function (p) {
      return api.similar(p.id).then(function (list) { return { p: p, list: list }; });
    })).then(function (r) {
      var mine = designOf(r.p.title);
      var list = dedupeDesigns(r.list.filter(function (x) { return x.handle !== r.p.handle && designOf(x.title) !== mine; }));
      if (!list.length) {
        bot('I couldn’t find anything similar right now. Want to try one of these?');
        return greetChips();
      }
      list.forEach(function (x) { if (!cache.product[x.handle]) cache.product[x.handle] = x; remember(x); });
      var ctx = state.ctx;
      ctx.list = list.map(function (x) { return x.handle; });
      ctx.offset = 0;
      ctx.focus = r.p.handle;
      var names = list.slice(0, 2).map(function (x) { return '**' + cap(designOf(x.title).replace(/\b\w/g, function (c) { return c.toUpperCase(); })) + '**'; });
      return showBatch('Same energy as ' + r.p.title.split(/\s+[-–—|]\s+/)[0] + ': ' + names.join(' & ') + ' 👇');
    }).catch(function () { failSoft(); });
  }

  /* Add to cart: pick colour/style, ask size with chips if needed */
  function handleAdd(handle, opts) {
    opts = opts || {};
    return think(api.product(handle), 350).then(function (p) {
      state.ctx.focus = p.handle;
      var f = state.ctx.filters || {};
      var color = opts.color || (f.color && f.color !== 'any' ? f.color : null) || selectedColorOf(p);
      var style = opts.style || selectedStyleOf(p);
      var styleKindWanted = style ? styleKind(style) : (f.type === 'hoodie' ? 'hoodie' : null);
      var si = optIndex(p, RE_SIZE_OPT);
      var base = variantsMatching(p, { color: color, style: styleKindWanted });
      if (!base.length) base = variantsMatching(p, { color: color });
      if (!base.length) base = p.variants;

      if (si < 0 || p.variants.length === 1) {
        var only = base.filter(function (v) { return v.available; })[0];
        if (!only) { bot('This one is sold out right now 😕'); return; }
        return doAdd(p, only, null);
      }
      if (!opts.size) {
        var sizesAll = p.options[si].values.filter(function (s) {
          return base.some(function (v) { return normSize(v.o[si]) === normSize(s) && v.available; });
        });
        if (!sizesAll.length) {
          bot('**' + p.title + '** is sold out right now 😕 Want to see something similar?');
          chips([{ label: 'Similar designs', act: { do: 'similar', handle: p.handle } }]);
          return;
        }
        var items = sizesAll.map(function (s) {
          return { label: s, size: true, aria: s, act: { do: 'addSize', handle: p.handle, size: s, color: color, style: style } };
        });
        state.ctx.pending = { type: 'pick_size', handle: p.handle, color: color, style: style };
        save();
        var colTxt = color ? ' (' + color + ')' : '';
        bot('**' + p.title + '**' + colTxt + ' — which size? 👇');
        chips(items);
        if (size.chartKeyFor(p, style)) chips([{ label: 'Not sure? Find my size', text: 'Find my size', act: { do: 'sizeFor', handle: p.handle } }]);
        return;
      }
      var v = base.filter(function (x) { return normSize(x.o[si]) === normSize(opts.size); });
      var avail = v.filter(function (x) { return x.available; })[0];
      if (!v.length) {
        bot('This one doesn’t come in ' + opts.size + '. Here are the sizes:');
        return handleAdd(handle, { color: color, style: style });
      }
      if (!avail) {
        bot('Oops, **' + opts.size + '** is sold out 😕 Pick another size?');
        return handleAdd(handle, { color: color, style: style });
      }
      return doAdd(p, avail, opts.size);
    }).catch(function () { failSoft(); });
  }

  function doAdd(p, v, sizeLabel) {
    return think(api.add(v.id, 1), 400).then(function () {
      state.ctx.pending = null;
      var si = optIndex(p, RE_SIZE_OPT);
      push({ k: 'confirm', title: p.title, size: sizeLabel || (si > -1 ? v.o[si] : v.title), price: v.price });
      track('a09_chat_add_to_cart', {
        product_title: p.title,
        variant_title: v.title || null,
        price: v.price,
        quantity: 1,
        currency: CFG.currency || 'INR'
      });
      return refreshCart(true).then(markChatCart);
    }).catch(function (e) {
      bot('Couldn’t add it to the cart: ' + (e && e.message ? e.message : 'try again') + '.');
    });
  }

  /* Size recommendation flow */
  function handleSize(u) {
    var ctx = state.ctx;
    var pend = ctx.pending && (ctx.pending.type === 'measures' || ctx.pending.type === 'fit') ? ctx.pending : null;
    var s = pend ? Object.assign({}, pend) : { type: 'measures' };
    if (u.measures) {
      if (u.measures.heightCm) s.heightCm = u.measures.heightCm;
      if (u.measures.weightKg) s.weightKg = u.measures.weightKg;
    }
    if (u.fit) { s.fit = u.fit; s.fitChosen = true; }
    if (!s.handle && !s.chartKey) {
      var h = resolveRef(u);
      if (h) s.handle = h;
    }
    var prodP = s.handle ? api.product(s.handle).catch(function () { return null; }) : Promise.resolve(null);
    return prodP.then(function (p) {
      if (p && !s.chartKey) {
        s.style = selectedStyleOf(p);
        var key = size.chartKeyFor(p, s.style);
        if (!key) {
          ctx.pending = null; save();
          bot('**' + p.title + '** doesn’t come in sizes. One size fits all 😄');
          return;
        }
        s.chartKey = key;
        s.color = selectedColorOf(p) || (ctx.filters && ctx.filters.color !== 'any' ? ctx.filters.color : null);
        var si = optIndex(p, RE_SIZE_OPT);
        s.sizes = si > -1 ? p.options[si].values : null;
        s.title = p.title;
      }
      if (!s.chartKey) {
        ctx.pending = s; save();
        bot('Size for which item? 👇');
        chips([
          { label: 'Regular tee', act: { do: 'chartPick', key: 'regular_tee' } },
          { label: 'Oversized tee', act: { do: 'chartPick', key: 'oversized_tee' } },
          { label: 'Hoodie', act: { do: 'chartPick', key: 'hoodie' } },
          { label: 'Oversize hoodie', act: { do: 'chartPick', key: 'oversized_hoodie' } }
        ]);
        return;
      }
      if (!(s.heightCm && s.weightKg)) {
        s.type = 'measures';
        ctx.pending = s; save();
        var missing = !s.heightCm && !s.weightKg ? 'Tell me your height and weight' : (!s.heightCm ? 'And your height?' : 'And your weight?');
        bot(missing + ' — e.g. **5\'10 and 75 kg** 📏');
        chips([{ label: 'See size chart', act: { do: 'chart', key: s.chartKey } }]);
        return;
      }
      /* no fit question: default to the regular (relaxed) fit unless the customer typed one */
      if (!s.fit) s.fit = 'relaxed';
      return think(Promise.resolve(), 650).then(function () { finishSize(s); });
    }).catch(function () { failSoft(); });
  }

  function finishSize(s) {
    var ctx = state.ctx;
    var rec = size.recommend({ chartKey: s.chartKey, heightCm: s.heightCm, weightKg: s.weightKg, chestIn: s.chestIn, fit: s.fit, availableSizes: s.sizes });
    ctx.pending = null;
    if (!rec) { save(); bot('I couldn’t work out a size. Here’s the size chart 👇'); push({ k: 'chart', key: s.chartKey }); return; }
    var chart = rec.chart;
    var fitWord = { snug: 'snug', relaxed: 'relaxed', baggy: 'extra baggy' }[s.fit];
    var bodyBits = s.chestIn ? 'your ' + s.chestIn + '" chest' : 'your height, weight';
    var why = s.fitChosen
      ? 'Based on ' + bodyBits + ', your “' + fitWord + '” preference and **the A09 size chart for this ' + chart.label + '**.'
      : 'Based on ' + bodyBits + ' and **the A09 size chart for this ' + chart.label + '**.';
    var pills = [];
    if (s.chestIn) pills.push('Chest ' + s.chestIn + '"');
    else pills.push(fmtHeight(s.heightCm) + ' · ' + Math.round(s.weightKg) + ' kg');
    if (s.fitChosen) pills.push(cap(fitWord) + ' fit');
    pills.push('A09 size chart');
    var alt = null;
    if (rec.alt) {
      alt = 'Between **' + (s.fit === 'snug' ? rec.size : rec.alt) + '** and **' + (s.fit === 'snug' ? rec.alt : rec.size) + '**. ' +
        (s.fit === 'snug' ? rec.alt + ' if you like it looser.' : rec.alt + ' if you want it a bit closer.');
    } else if (rec.edge === 'big') {
      alt = rec.size + ' is our roomiest size for this ' + chart.label + '.';
    } else if (rec.edge === 'small') {
      alt = rec.size + ' is our smallest size here — it’ll sit a little roomy.';
    }
    /* stock check on the real product, so we never push a sold-out size */
    var prod = s.handle ? cache.product[s.handle] : null, stock = null, ctaSize = rec.size, altSize = rec.alt, soldOut = false;
    if (prod) {
      var psi = optIndex(prod, RE_SIZE_OPT);
      if (psi > -1) {
        stock = {};
        var pvs = variantsMatching(prod, { color: s.color, style: s.style ? styleKind(s.style) : null });
        if (!pvs.length) pvs = prod.variants;
        pvs.forEach(function (v) { var k = normSize(v.o[psi]); stock[k] = !!(stock[k] || v.available); });
      }
    }
    if (stock && stock[normSize(rec.size)] === false) {
      soldOut = true;
      var order = chart.sizes, oi = order.indexOf(rec.size);
      var tryIdx = s.fit === 'snug' ? [oi - 1, oi + 1] : [oi + 1, oi - 1];
      ctaSize = tryIdx.map(function (j) { return order[j]; }).filter(function (z) { return z && stock[normSize(z)]; })[0] || null;
      altSize = null;
      alt = 'Heads up: **' + rec.size + '** is sold out in this one right now.' + (ctaSize ? ' Nearest in stock: **' + ctaSize + '**.' : '');
    } else if (altSize && stock && !stock[normSize(altSize)]) {
      altSize = null;
    }
    if (s.title) push({ k: 'context', t: 'For ' + s.title });
    push({ k: 'rec', size: rec.size, why: why, pills: pills, alt: alt, altSize: altSize, ctaSize: ctaSize, soldOut: soldOut, handle: s.handle || null, color: s.color || null, style: s.style || null });
    save();
    if (soldOut && !ctaSize && s.handle) chips([{ label: 'Similar designs', act: { do: 'similar', handle: s.handle } }, { label: 'See size chart', act: { do: 'chart', key: s.chartKey } }]);
    else if (!s.handle) chips([{ label: 'Show ' + chart.label + 's', text: 'Show ' + (s.chartKey.indexOf('hoodie') > -1 ? (s.chartKey === 'oversized_hoodie' ? 'oversized hoodies' : 'hoodies') : (s.chartKey === 'oversized_tee' ? 'oversized tees' : 'tees')) }, { label: 'See size chart', act: { do: 'chart', key: s.chartKey } }]);
    else chips([{ label: 'See size chart', act: { do: 'chart', key: s.chartKey } }]);
  }

  function handleSizeChart(u) {
    var h = resolveRef(u);
    var p0 = h ? api.product(h).catch(function () { return null; }) : Promise.resolve(null);
    return p0.then(function (p) {
      var key = p ? size.chartKeyFor(p, selectedStyleOf(p)) : null;
      if (p && !key) { bot('This product doesn’t come in sizes 🙂'); return; }
      if (key) { bot('A09 size chart for **' + p.title + '** 👇'); push({ k: 'chart', key: key }); }
      else { bot('Here’s the A09 size chart 👇'); ['regular_tee', 'oversized_tee', 'hoodie', 'oversized_hoodie'].forEach(function (k) { push({ k: 'chart', key: k }); }); }
      chips([{ label: 'Find my size', text: 'Find my size' }]);
    });
  }

  function greetChips() {
    var items = [
      { label: 'Latest Collections', act: { do: 'latest' } },
      { label: 'Under ₹1,500', text: 'Tees under ₹1500' },
      { label: 'Hoodies', text: 'Show hoodies' },
      { label: 'Find my size', text: 'Find my size' }
    ];
    if (trackEnabled()) items.push({ label: 'Track order', act: { do: 'trackOrder' } });
    chips(items);
  }
  function greet() {
    var p = pageProduct();
    bot('Hey! I’m the A09 assistant 👋 Bandra-made tees, hoodies and desi-vibe merch. What are you looking for?');
    if (p) push({ k: 'context', t: 'On the ' + p.title + ' page' });
    if (p) chips([
      { label: 'Find my size', text: 'What size should I take?' },
      { label: 'Details', act: { do: 'details', handle: p.handle } },
      { label: 'Similar', act: { do: 'similar', handle: p.handle } },
      { label: 'Latest Collections', act: { do: 'latest' } }
    ]);
    else greetChips();
  }
  function fallback() {
    bot('Hmm, I didn’t get that 🤔 Try “black hoodies under ₹2000”, or pick one below:');
    greetChips();
    bot('Or talk to us directly:');
    push({ k: 'contact' });
  }
  function failSoft() {
    typing(false);
    bot('The network is a bit slow 😅 Please try again, or talk to us directly:');
    push({ k: 'contact' });
  }

  /* ==================================================================
   * Track Order
   * ==================================================================
   * Shows PLACED orders (not the cart). Data comes from the a09-assistant-api
   * Worker through Shopify's App Proxy on this same domain:
   *   GET  {base}/orders         logged-in customer's latest orders
   *   POST {base}/orders/lookup  guest: order number + email
   * Shopify signs each proxied request and tells the Worker who is logged in,
   * so the browser never sends (and cannot fake) a customer id.
   *
   * Rules:
   *   1. Real data only. The expected-delivery row renders only when Shopify
   *      has an estimatedDeliveryAt. No tracking number -> no courier rows.
   *   2. Never persist order data. These messages are `ephemeral`: save() drops
   *      them, and selection / guest-flow state lives only in memory.
   * ================================================================== */
  var TRACK_CFG = CFG.trackOrder || {};
  var trackSel = {};               /* ticked orders/items, in memory only */
  var lastOrders = [];             /* last order list shown, in memory only */
  var trackFlow = { step: null, number: null };   /* guest lookup, in memory only */

  function trackEnabled() { return TRACK_CFG.enabled === true; }
  function maxOrders() {
    var n = Number(TRACK_CFG.maxOrders);
    return n > 0 && n <= 20 ? n : 5;
  }
  /* Same-origin path only (the App Proxy). Never a full URL to another host. */
  function trackBase() {
    var b = String(TRACK_CFG.base || '/apps/a09').replace(/\/+$/, '');
    return /^\/[a-z0-9/_-]+$/i.test(b) && b.indexOf('//') < 0 ? b : '/apps/a09';
  }
  function resetTrack() {
    trackSel = {};
    lastOrders = [];
    trackFlow = { step: null, number: null };
  }

  function accountUrl() { return route('account', '/account'); }

  /* ---- Status pill: every documented FulfillmentDisplayStatus ---- */
  var PACKING = { label: 'Order placed · Packing', tone: 'pending' };
  var CANCELLED = { label: 'Cancelled', tone: 'cancelled' };
  var DISPLAY_STATUS = {
    CONFIRMED: { label: 'Shipping soon', tone: 'pending' },
    SUBMITTED: { label: 'Shipping soon', tone: 'pending' },
    LABEL_PRINTED: { label: 'Shipping soon', tone: 'pending' },
    LABEL_PURCHASED: { label: 'Shipping soon', tone: 'pending' },
    /* Handed over with no courier scan yet: "Shipped", not "In Transit" --
     * the pill must not claim more than Shopify knows. */
    FULFILLED: { label: 'Shipped', tone: 'moving' },
    MARKED_AS_FULFILLED: { label: 'Shipped', tone: 'moving' },
    CARRIER_PICKED_UP: { label: 'In Transit', tone: 'moving' },
    IN_TRANSIT: { label: 'In Transit', tone: 'moving' },
    DELAYED: { label: 'In Transit · Delayed', tone: 'problem' },
    OUT_FOR_DELIVERY: { label: 'Out for delivery', tone: 'moving' },
    READY_FOR_PICKUP: { label: 'Ready for pickup', tone: 'moving' },
    DELIVERED: { label: 'Delivered', tone: 'done' },
    PICKED_UP: { label: 'Delivered', tone: 'done' },
    ATTEMPTED_DELIVERY: { label: 'Delivery attempted', tone: 'problem' },
    NOT_DELIVERED: { label: 'Delivery issue', tone: 'problem' },
    FAILURE: { label: 'Delivery issue', tone: 'problem' },
    CANCELED: CANCELLED,
    LABEL_VOIDED: CANCELLED
  };
  /** A new Shopify value still shows, as its raw label: IN_TRANSIT -> "In transit". */
  function fulfillmentStatus(raw) {
    var key = String(raw || '').trim().toUpperCase();
    if (!key) return { label: 'Shipped', tone: 'moving' };
    if (DISPLAY_STATUS[key]) return DISPLAY_STATUS[key];
    var words = key.replace(/[_-]+/g, ' ').toLowerCase();
    return { label: words.charAt(0).toUpperCase() + words.slice(1), tone: 'moving' };
  }
  /** Order-level pill: cancelled, else the latest shipment, else packing. */
  function orderStatus(o) {
    if (o.cancelled) return CANCELLED;
    var fs = o.fulfillments || [];
    return fs.length ? fulfillmentStatus(fs[fs.length - 1].displayStatus) : PACKING;
  }
  function pill(st) {
    return el('span', { class: 'a09-pill a09-pill--' + st.tone, text: st.label });
  }

  /** Real dates only; an invalid or missing one renders nothing. */
  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try { return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch (e) { return d.toISOString().slice(0, 10); }
  }

  function itemLabel(it) {
    return (it.title || 'Item') + (it.variantTitle ? ' · ' + it.variantTitle : '') +
      (Number(it.quantity) > 1 ? ' × ' + it.quantity : '');
  }
  function orderKey(o) { return String(o && o.name ? o.name : ''); }
  /** The number as shown to the customer: A092027, never #A092027. */
  function orderLabel(o) { return orderKey(o).replace(/^#\s*/, ''); }
  function itemKey(o, i) { return orderKey(o) + '|' + i; }

  /** Line-item indexes not (fully) covered by any shipment yet. */
  function unshippedIndexes(o) {
    var shipped = {};
    (o.fulfillments || []).forEach(function (f) {
      (f.items || []).forEach(function (x) { shipped[x.index] = (shipped[x.index] || 0) + (Number(x.quantity) || 1); });
    });
    var out = [];
    (o.lineItems || []).forEach(function (it, i) { if ((shipped[i] || 0) < (Number(it.quantity) || 1)) out.push(i); });
    return out;
  }

  /* ---- Renderers ---- */

  function tickBtn(key, label, cls, aria) {
    var b = el('button', {
      type: 'button',
      class: 'a09-tick' + (cls ? ' ' + cls : ''),
      'aria-pressed': trackSel[key] ? 'true' : 'false',
      'aria-label': aria || null
    }, [el('span', { class: 'a09-tick__box', 'aria-hidden': 'true' }), el('span', { text: label })]);
    b.addEventListener('click', function () {
      trackSel[key] = !trackSel[key];
      b.setAttribute('aria-pressed', trackSel[key] ? 'true' : 'false');
    });
    return b;
  }

  function renderOrders(orders) {
    var wrap = el('div', { class: 'a09-orders', role: 'list', 'aria-label': 'Your recent orders' });
    orders.forEach(function (o) {
      var items = o.lineItems || [];
      var count = items.reduce(function (n, it) { return n + (Number(it.quantity) || 1); }, 0);
      var ok = orderKey(o);
      var meta = [o.createdAt && fmtDate(o.createdAt) ? 'Ordered ' + fmtDate(o.createdAt) : '', count + ' item' + (count === 1 ? '' : 's')]
        .filter(Boolean).join(' · ');
      wrap.appendChild(el('div', { class: 'a09-order', role: 'listitem' }, [
        el('div', { class: 'a09-order__head' }, [tickBtn(ok, orderLabel(o), '', 'Select order ' + orderLabel(o)), pill(orderStatus(o))]),
        el('p', { class: 'a09-order__meta', text: meta }),
        el('div', { class: 'a09-order__thumbs', 'aria-hidden': 'true' }, items.slice(0, 4).map(function (it) {
          return it.image
            ? el('img', { src: img(it.image, 120), alt: '', loading: 'lazy', width: '40', height: '40' })
            : el('span', { class: 'a09-order__thumb--none' });
        })),
        /* One order can arrive in several parcels, so each item can be picked. */
        el('ul', { class: 'a09-order__items' }, items.map(function (it, i) {
          return el('li', null, [tickBtn(itemKey(o, i), itemLabel(it), 'a09-tick--item', null)]);
        }))
      ]));
    });
    return wrap;
  }

  function policyLine() {
    var p = (CFG.policy && CFG.policy.trackGeneral) || '';
    return p ? el('p', { class: 'a09-track__policy', text: p }) : null;
  }
  function detailsBtn(o, solid) {
    return o.statusPageUrl
      ? el('a', { class: 'a09-btn' + (solid ? ' a09-btn--solid' : ''), href: o.statusPageUrl, target: '_blank', rel: 'noopener', text: 'Order details' })
      : null;
  }

  /**
   * Tracking cards for one order: one per shipment, plus one for items not yet
   * shipped. `only` (item indexes) narrows it to the items the customer ticked.
   */
  function renderTracking(o, only) {
    var wrap = el('div', { class: 'a09-tracks' });
    var items = o.lineItems || [];
    var want = function (i) { return !only || only.indexOf(i) > -1; };
    var shipments = (o.fulfillments || []).filter(function (f) {
      return !only || (f.items || []).some(function (x) { return want(x.index); });
    });
    var many = (o.fulfillments || []).length > 1;

    shipments.forEach(function (f) {
      var titles = (f.items || []).filter(function (x) { return want(x.index); })
        .map(function (x) { return items[x.index] ? itemLabel(items[x.index]) : ''; }).filter(Boolean).join(', ');
      var rows = [];
      /* RULE: this row exists only when Shopify has a real date. */
      var eta = fmtDate(f.estimatedDeliveryAt);
      if (eta) rows.push(el('p', { class: 'a09-track__row a09-track__row--eta' }, [el('span', { class: 'a09-track__k', text: 'Expected delivery' }), el('strong', { text: eta })]));
      var delivered = fmtDate(f.deliveredAt);
      if (delivered) rows.push(el('p', { class: 'a09-track__row' }, [el('span', { class: 'a09-track__k', text: 'Delivered on' }), el('strong', { text: delivered })]));

      /* Courier rows and link only when there is a tracking number. */
      var actions = [];
      (f.trackingInfo || []).filter(function (t) { return t && t.number; }).forEach(function (t) {
        if (t.company) rows.push(el('p', { class: 'a09-track__row' }, [el('span', { class: 'a09-track__k', text: 'Courier' }), el('span', { text: t.company })]));
        rows.push(el('p', { class: 'a09-track__row' }, [el('span', { class: 'a09-track__k', text: 'Tracking no.' }), el('code', { class: 'a09-track__num', text: t.number })]));
        if (t.url) actions.push(el('a', { class: 'a09-btn a09-btn--solid', href: t.url, target: '_blank', rel: 'noopener', text: 'Track on courier site →' }));
      });
      var details = detailsBtn(o, false);
      if (details) actions.push(details);

      var idx = (o.fulfillments || []).indexOf(f) + 1;
      wrap.appendChild(el('div', { class: 'a09-track' }, [
        el('div', { class: 'a09-track__head' }, [el('strong', { text: orderLabel(o) + (many ? ' · Parcel ' + idx : '') }), pill(fulfillmentStatus(f.displayStatus))]),
        titles ? el('p', { class: 'a09-track__items', text: titles }) : null,
        rows.length ? el('div', { class: 'a09-track__rows' }, rows) : null,
        actions.length ? el('div', { class: 'a09-track__actions' }, actions) : null,
        policyLine()
      ]));
    });

    /* Items not shipped yet (or a cancelled order). */
    var pending = unshippedIndexes(o).filter(want);
    if (pending.length || (!shipments.length && !only)) {
      var st = o.cancelled ? CANCELLED : PACKING;
      var ptitles = pending.map(function (i) { return itemLabel(items[i]); }).join(', ');
      var pdetails = detailsBtn(o, true);
      wrap.appendChild(el('div', { class: 'a09-track' }, [
        el('div', { class: 'a09-track__head' }, [el('strong', { text: orderLabel(o) }), pill(st)]),
        ptitles ? el('p', { class: 'a09-track__items', text: ptitles }) : null,
        o.cancelled ? null : el('p', { class: 'a09-track__note', text: 'Not dispatched yet, so there’s no tracking number. It will show here as soon as it ships.' }),
        pdetails ? el('div', { class: 'a09-track__actions' }, [pdetails]) : null,
        o.cancelled ? null : policyLine()
      ]));
    }
    return wrap;
  }

  /* ---- Network (same origin, through the App Proxy) ---- */

  function trackError() {
    bot('Couldn’t load tracking right now. Please try again in a little while 🙏');
    chips([{ label: 'View my orders →', href: accountUrl() }]);
  }

  function fetchOrders() {
    return getJSON(trackBase() + '/orders');
  }

  /* ---- Flows ---- */

  function handleTrackOrder() {
    if (!trackEnabled()) {
      bot('Order tracking isn’t available here right now. Our team will check it for you 👇');
      return push({ k: 'contact' });
    }
    trackSel = {};
    trackFlow = { step: null, number: null };
    return think(fetchOrders()).then(function (res) {
      if (!res || res.loggedIn !== true) return startGuest();
      var orders = (Array.isArray(res.orders) ? res.orders : []).slice(0, maxOrders());
      lastOrders = orders;
      if (!orders.length) {
        bot('No orders found yet 🙂');
        return showLatest();
      }
      bot('Which order would you like to track? 📦');
      push({ k: 'orders', ephemeral: true, orders: orders });
      bot('Tick an order or items, or see them all at once 👇');
      chips([
        { label: 'Track selected', act: { do: 'trackSelected' } },
        { label: 'Track all', act: { do: 'trackAll' } }
      ]);
    }, trackError);
  }

  function showTracking(picks) {
    if (!picks.length) { bot('Please tick an order or an item first 🙂'); return; }
    picks.forEach(function (p) { push({ k: 'tracking', ephemeral: true, order: p.order, only: p.only || null }); });
  }
  function trackAll() {
    showTracking(lastOrders.map(function (o) { return { order: o }; }));
  }
  /** A ticked order tracks all of it; ticked items track just their parcels. */
  function trackSelected() {
    var picks = [];
    lastOrders.forEach(function (o) {
      if (trackSel[orderKey(o)]) return picks.push({ order: o });
      var only = [];
      (o.lineItems || []).forEach(function (_it, i) { if (trackSel[itemKey(o, i)]) only.push(i); });
      if (only.length) picks.push({ order: o, only: only });
    });
    showTracking(picks);
  }

  /* Guest: order number, then email, then one lookup. */
  var RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Store order numbers look like A092027. Accepts "a092027", "#A092027",
   * "# A09 2027" and surrounding spaces; returns "A092027", or null if it is
   * not a plausible order number. Spaces are only removed inside an A09 number,
   * so "show 2 hoodies" is never read as one.
   */
  function normOrderNo(v) {
    var s = String(v || '').trim().replace(/^#\s*/, '');
    if (/^a09[\s\d]+$/i.test(s)) s = s.replace(/\s+/g, '');
    return /^[a-z0-9][a-z0-9-]{0,19}$/i.test(s) && /\d/.test(s) ? s.toUpperCase() : null;
  }

  function startGuest() {
    trackFlow = { step: 'number', number: null };
    bot('Please send your **order number** (e.g. A090001). You’ll find it in your order confirmation email 📦');
  }

  /**
   * Handles a typed reply while the guest flow waits. Returns a promise or true
   * when it handled the text, or null to let normal routing take it (the
   * customer changed the subject, e.g. "hoodies dikhao").
   */
  function guestStep(text, u) {
    var v = String(text || '').trim();
    var other = u && u.intent && u.intent !== 'unknown' && u.intent !== 'track_order';

    if (/^(cancel|stop|chhodo|chodo|rehne\s+do|rahne\s+do|nahi|no)$/i.test(v)) {
      trackFlow = { step: null, number: null };
      bot('Okay 👍 Let me know if you need anything else.');
      return true;
    }

    if (trackFlow.step === 'number') {
      var no = normOrderNo(v);
      if (no) {
        trackFlow = { step: 'email', number: no };
        bot('Now send the **email ID** you used for this order ✉️');
        return true;
      }
      if (other || !/\d/.test(v)) { trackFlow = { step: null, number: null }; return null; }
      bot('That order number doesn’t look right 🤔 Send it like this: A090001');
      return true;
    }

    if (trackFlow.step === 'email') {
      if (RE_EMAIL.test(v) && v.length <= 254) return guestLookup(trackFlow.number, v);
      if (v.indexOf('@') < 0 && other) { trackFlow = { step: null, number: null }; return null; }
      bot('That email doesn’t look right 🤔 For example: name@gmail.com');
      return true;
    }
    trackFlow = { step: null, number: null };
    return null;
  }

  function guestLookup(number, email) {
    trackFlow = { step: null, number: null };
    var req = getJSON(trackBase() + '/orders/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ orderNumber: number, email: email })
    });
    return think(req).then(function (res) {
      if (!res || !res.order) return trackError();
      lastOrders = [res.order];
      bot('Found it! 👇');
      push({ k: 'tracking', ephemeral: true, order: res.order });
    }, function (e) {
      if (e && e.status === 404) {
        bot('We couldn’t find an order with this order number and email 🤔 Please check both and try again.');
        return chips([{ label: 'Try again', act: { do: 'trackGuest' } }]);
      }
      if (e && e.status === 429) {
        bot('Too many tries 🙏 Please try again in 10 minutes, or talk to our team:');
        return push({ k: 'contact' });
      }
      if (e && e.status === 400) {
        bot('The order number or email doesn’t look right 🤔');
        return chips([{ label: 'Try again', act: { do: 'trackGuest' } }]);
      }
      trackError();
    });
  }

  A09.orders = {
    enabled: trackEnabled,
    status: fulfillmentStatus,
    orderStatus: orderStatus,
    fmtDate: fmtDate,
    isTrackOrder: isTrackOrder
  };

  /* ------------------------------------------------------------------ *
   * Routing
   * ------------------------------------------------------------------ */
  function context() {
    var c = state.ctx;
    return {
      pending: c.pending,
      hasList: !!(c.filters && c.list.length),
      hasShown: c.shown.length > 0,
      filters: c.filters,
      shownTitles: c.shown.map(function (h) { return c.seen[h] ? c.seen[h].title : h; }),
      product: pageProduct()
    };
  }

  function handle(text) {
    return A09.ai.understand(text, context()).then(function (u) {
      u = u || { intent: 'unknown', filters: {} };
      u.filters = u.filters || {};
      var ctx = state.ctx;
      /* Mid guest lookup: the reply is an order number or an email. */
      if (trackFlow.step) {
        var tf = guestStep(text, u);
        if (tf) return tf === true ? undefined : tf;
      }
      /* a lone size while we wait for one, e.g. "L" */
      if (ctx.pending && ctx.pending.type === 'pick_size') {
        var lone = String(text).trim().match(/^(xs|s|m|l|xl|xxl|xxxl|[2-5]xl|small|medium|large)$/i);
        if (lone || (u.size && u.ref == null && !(u.keywords || []).length)) {
          var p = ctx.pending;
          return handleAdd(p.handle, { size: u.size || normSize(lone[1]), color: p.color, style: p.style });
        }
      }
      if (ctx.pending && (ctx.pending.type === 'measures' || ctx.pending.type === 'fit') &&
          (u.measures || (ctx.pending.type === 'fit' && u.fit)) && u.intent !== 'browse' && u.intent !== 'add') {
        return handleSize(u);
      }
      switch (u.intent) {
        case 'reset': return resetChat();
        case 'greet': bot('Hello hello! 👋 What can I show you?'); return greetChips();
        case 'help':
          bot('I can find products, filter them (price, colour, oversized), add them to your cart and suggest your size. Try:');
          return chips([{ label: 'Hoodies under ₹2,000', text: 'Show hoodies under ₹2000' }, { label: 'Mumbai food tees', text: 'Show me Mumbai food designs' }, { label: 'Find my size', text: 'Find my size' }]);
        case 'thanks': bot('Anytime! 🙌 Let me know if you need anything else.'); return;
        case 'contact': bot('Talk to our team directly 👇'); return push({ k: 'contact' });
        case 'exchange': bot(((CFG.policy && CFG.policy.exchange) || 'Returns within 14 days of receiving your order.') + (CFG.policy && CFG.policy.exchangeMore ? ' ' + CFG.policy.exchangeMore : '')); return push({ k: 'contact' });
        case 'shipping': bot((CFG.policy && CFG.policy.shipping) || 'Free shipping over ₹2,000.'); return;
        case 'track_order': return handleTrackOrder();
        case 'checkout': bot('Let’s check out 👇'); return chips([{ label: 'Go to checkout →', href: route('checkout', '/checkout'), ev: 'a09_chat_checkout_click' }, { label: 'View cart', href: route('cart', '/cart'), ev: 'a09_chat_view_cart' }]);
        case 'show_cart':
          return think(api.cart()).then(function (c) {
            setCount(c.item_count || 0);
            if (!c.item_count) { bot('Your cart is empty 🛒 Want me to show you something?'); return greetChips(); }
            bot('Your cart has ' + c.item_count + ' item' + (c.item_count > 1 ? 's' : '') + ', total **' + money(c.total_price / 100) + '**.');
            chips([{ label: 'Go to checkout →', href: route('checkout', '/checkout'), ev: 'a09_chat_checkout_click' }, { label: 'View cart', href: route('cart', '/cart'), ev: 'a09_chat_view_cart' }]);
          }).catch(function () { failSoft(); });
        case 'more': return handleMore();
        case 'latest': return showLatest();
        case 'size_chart': return handleSizeChart(u);
        case 'size_rec':
        case 'fit_answer':
          return handleSize(u);
        case 'details': { var hd = needProduct(u); return hd ? handleDetails(hd) : null; }
        case 'similar': { var hs = needProduct(u); return hs ? handleSimilar(hs) : null; }
        case 'view': {
          var hv = needProduct(u);
          if (!hv) return;
          var pv = cache.product[hv];
          bot('Opening ' + (pv ? '**' + pv.title + '**' : 'the page') + '… the chat stays right here 👍');
          setTimeout(function () { location.href = pv ? pv.url : ROOT + '/products/' + hv; }, 500);
          return;
        }
        case 'add': {
          var ha = resolveRef(u);
          if (!ha && ctx.pending && ctx.pending.type === 'pick_size') ha = ctx.pending.handle;
          if (!ha) {
            if (Object.keys(u.filters).length || u.keywords.length) return handleBrowse(u);
            bot(ctx.shown.length ? 'Which one should I add? Say “first one”, “2nd”… 🙂' : 'Let’s pick something first 👇');
            if (!ctx.shown.length) greetChips();
            return;
          }
          ctx.focus = ha;
          return handleAdd(ha, { size: u.size, color: u.filters.color && u.filters.color !== 'any' ? u.filters.color : null });
        }
        case 'browse': return handleBrowse(u);
        default: return fallback();
      }
    }).catch(function (e) {
      if (window.console && console.warn) console.warn('[A09 assistant]', e);
      failSoft();
    });
  }

  /* Chips / card buttons */
  function act(a, label) {
    if (busy) return;
    if (label) user(label);
    return run(function () {
      var ctx = state.ctx;
      switch (a.do) {
        case 'details': return handleDetails(a.handle);
        case 'similar': return handleSimilar(a.handle);
        case 'add': return handleAdd(a.handle, { color: a.color });
        case 'addSize': return handleAdd(a.handle, { size: a.size, color: a.color, style: a.style });
        case 'sizeFor':
          ctx.pending = { type: 'measures', handle: a.handle };
          return handleSize({ intent: 'size_rec', filters: {} });
        case 'fit':
          return handleSize({ intent: 'fit_answer', fit: a.fit, filters: {} });
        case 'chartPick':
          ctx.pending = Object.assign({}, ctx.pending || { type: 'measures' }, { chartKey: a.key, handle: null });
          return handleSize({ intent: 'size_rec', filters: {} });
        case 'chart': push({ k: 'chart', key: a.key }); return;
        case 'latest': return showLatest();
        case 'trackOrder': return handleTrackOrder();
        case 'trackGuest': return startGuest();
        case 'trackAll': return trackAll();
        case 'trackSelected': return trackSelected();
        case 'collection': return handleBrowse({ filters: { collection: a.handle, collectionName: a.name }, keywords: [] });
        case 'relax':
          if (ctx.filters) { delete ctx.filters[a.drop]; }
          return handleBrowse({ refine: true, filters: {}, keywords: ctx.keywords || [] });
      }
    });
  }
  function onChip(c) {
    if (c.act) return act(c.act, c.label);
    if (c.text) return send(c.text);
  }
  function run(fn) {
    busy = true;
    var p;
    try { p = Promise.resolve(fn()); } catch (e) { p = Promise.reject(e); }
    return p.catch(function (e) {
      if (window.console && console.warn) console.warn('[A09 assistant]', e);
      failSoft();
    }).then(function () { busy = false; save(); });
  }
  function send(text) {
    text = String(text || '').trim();
    if (!text || busy) return;
    /* An order number or email typed during the guest lookup is never saved. */
    user(text, !!trackFlow.step);
    return run(function () { return handle(text); });
  }
  function userOpen() {
    var wasOpen = state.open && panel && !panel.hidden;
    openPanel(true);
    if (!wasOpen) track('a09_chat_open');
  }
  A09.assistant = { send: send, open: userOpen, close: closePanel, state: state, reset: function () { resetChat(); } };

  function resetChat() {
    resetTrack();
    state.msgs = [];
    state.ctx = freshCtx();
    save();
    if (log) log.innerHTML = '';
    greet();
  }

  /* ------------------------------------------------------------------ *
   * Panel open / close + keyboard
   * ------------------------------------------------------------------ */
  var mq = null;
  try { mq = window.matchMedia('(max-width: 640px)'); } catch (e) {}
  function lockScroll(on) {
    document.documentElement.classList.toggle('a09-lock', !!(on && mq && mq.matches));
  }
  function openPanel(focusInput) {
    if (!panel) return;
    panel.hidden = false;
    root.classList.add('is-open');
    launcher.setAttribute('aria-expanded', 'true');
    if (!reduceMotion) {
      panel.classList.remove('is-entering');
      void panel.offsetWidth;
      panel.classList.add('is-entering');
    }
    state.open = true;
    save();
    lockScroll(true);
    if (!state.msgs.length) greet();
    scrollDown();
    if (focusInput !== false) setTimeout(function () { input && input.focus({ preventScroll: true }); }, 60);
  }
  function closePanel() {
    if (!panel) return;
    panel.hidden = true;
    root.classList.remove('is-open');
    launcher.setAttribute('aria-expanded', 'false');
    state.open = false;
    save();
    lockScroll(false);
    launcher.focus({ preventScroll: true });
  }

  /* Lift the launcher above sticky bottom bars (sticky add-to-cart, mobile nav, preview bar) */
  var liftEls = [], liftRaf = 0;
  function scanBars() {
    liftEls = [];
    try {
      document.querySelectorAll('body *').forEach(function (e) {
        if (e.closest('.a09-root')) return;
        var cs = getComputedStyle(e);
        if (cs.position !== 'fixed') return;
        var b = parseFloat(cs.bottom);
        if (isNaN(b) || b > 4) return;
        liftEls.push(e);
      });
    } catch (e) {}
    lift();
  }
  function lift() {
    if (liftRaf) return;
    liftRaf = requestAnimationFrame(function () {
      liftRaf = 0;
      var h = 0, vh = window.innerHeight, vw = window.innerWidth;
      liftEls.forEach(function (e) {
        var r = e.getBoundingClientRect();
        if (r.width < vw * 0.5 || r.height < 24 || r.height > 200 || r.top >= vh - 1 || r.bottom < vh - 4) return;
        var cs = getComputedStyle(e);
        if (cs.visibility === 'hidden' || cs.display === 'none' || parseFloat(cs.opacity) === 0) return;
        h = Math.max(h, vh - r.top);
      });
      if (root) root.style.setProperty('--a09-lift', Math.round(h) + 'px');
    });
  }

  function init() {
    root = $('[data-a09-root]');
    if (!root) return;
    launcher = $('[data-a09-launcher]', root);
    panel = $('[data-a09-panel]', root);
    log = $('[data-a09-log]', root);
    form = $('[data-a09-form]', root);
    input = $('[data-a09-input]', root);
    countEls = Array.prototype.slice.call(root.querySelectorAll('[data-a09-count]'));
    /* keep it out of the theme's own focus traps / drawers */
    if (root.parentNode !== document.body) document.body.appendChild(root);

    load();
    state.msgs.forEach(function (m) { renderMsg(m, false); });
    if (state.cartCount != null) setCount(state.cartCount);
    refreshCart(false);

    launcher.addEventListener('click', userOpen);
    trackedNav($('[data-a09-cart-link]', root), 'a09_chat_view_cart', 'header');
    $('[data-a09-close]', root).addEventListener('click', closePanel);
    $('[data-a09-reset]', root).addEventListener('click', function () { if (!busy) resetChat(); });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var v = input.value;
      input.value = '';
      send(v);
    });
    panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); closePanel(); }
    });
    if (mq && mq.addEventListener) mq.addEventListener('change', function () { lockScroll(state.open); });

    if (state.open) openPanel(false);
    setTimeout(scanBars, 800);
    setTimeout(scanBars, 3000);
    window.addEventListener('scroll', lift, { passive: true });
    window.addEventListener('resize', scanBars);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
